/*
 * NEI theme runtime script.
 *
 * Organization:
 * 1) Global storefront behavior (carousels, predictive search, account popover, cart drawer)
 * 2) Product page behavior (gallery, variants, quantity, add-to-cart, sticky tabs/scrollspy)
 * 3) Collection page behavior (mobile drawers, sort, view preference)
 * 4) Product mobile read-more behavior
 * 5) Quote modal + secure upload behavior
 */

/* ========================================
 * 1) Global storefront behavior
 * ====================================== */
(() => {
  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => Array.from(r.querySelectorAll(s));

  // ---------------------------
  // Carousels (product media, related products, etc): click buttons or drag to scroll, with snapping and wheel support
  // ---------------------------
  const initCarouselWrap = (wrap) => {
    const track = qs(".carousel-track", wrap);
    const buttons = qsa(".carousel-head-btn[data-carousel-dir]", wrap);
    if (!track || buttons.length < 2) return;

    const padLeft = () => {
      const p = parseFloat(getComputedStyle(track).paddingLeft || "0");
      return Number.isFinite(p) ? p : 0;
    };
    const getOffsets = () =>
      qsa(".product-col", track).map((c) => c.offsetLeft);
    const maxScrollLeft = () =>
      Math.max(0, track.scrollWidth - track.clientWidth);

    const offsetToScrollLeft = (offset) => Math.max(0, offset - padLeft());
    const nearestIndex = (scrollX, offsets) => {
      let best = 0,
        bestDist = Infinity;
      for (let i = 0; i < offsets.length; i++) {
        const target = offsetToScrollLeft(offsets[i]);
        const d = Math.abs(target - scrollX);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      }
      return best;
    };
    const scrollToIndex = (idx) => {
      const offsets = getOffsets();
      if (!offsets.length) return;
      const clamped = Math.max(0, Math.min(offsets.length - 1, idx));
      const target = offsetToScrollLeft(offsets[clamped]);
      track.scrollTo({ left: target, behavior: "smooth" });
    };
    const updateDisabled = () => {
      const max = maxScrollLeft();
      const x = track.scrollLeft;
      const prev = buttons.find((b) => b.dataset.carouselDir === "-1");
      const next = buttons.find((b) => b.dataset.carouselDir === "1");
      if (prev) prev.disabled = x <= 1;
      if (next) next.disabled = x >= max - 1;
    };
    const scrollByCard = (dir) => {
      const offsets = getOffsets();
      if (!offsets.length) return;
      const i = nearestIndex(track.scrollLeft, offsets);
      scrollToIndex(i + dir);
    };

    buttons.forEach((btn) =>
      btn.addEventListener("click", () => {
        const dir = parseInt(btn.dataset.carouselDir || "0", 10) || 0;
        if (!dir) return;
        scrollByCard(dir);
      }),
    );

    // Drag: grab/drag then snap (but keep links clickable)
    let isDown = false,
      isDragging = false,
      suppressClick = false;
    let startX = 0,
      startScroll = 0;

    track.style.cursor = "grab";

    // If the user dragged, suppress the click that browsers may fire after pointerup.
    track.addEventListener(
      "click",
      (e) => {
        if (!suppressClick) return;
        e.preventDefault();
        e.stopPropagation();
      },
      true,
    );

    track.addEventListener("pointerdown", (e) => {
      // Only left-click / primary touch
      if (typeof e.button === "number" && e.button !== 0) return;

      isDown = true;
      isDragging = false;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      track.style.cursor = "grabbing";
    });

    track.addEventListener("pointermove", (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;

      // Touch taps often drift a bit; require a bigger threshold on touch.
      const threshold = e.pointerType === "touch" ? 12 : 6;

      // Don't start a drag unless the pointer moved past the threshold.
      if (!isDragging && Math.abs(dx) > threshold) {
        isDragging = true;
        try {
          track.setPointerCapture(e.pointerId);
        } catch {}
      }

      if (isDragging) {
        track.scrollLeft = startScroll - dx;
      }
    });

    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      track.style.cursor = "grab";

      if (isDragging) {
        suppressClick = true;
        setTimeout(() => {
          suppressClick = false;
        }, 0);

        const offsets = getOffsets();
        if (offsets.length) {
          const i = nearestIndex(track.scrollLeft, offsets);
          scrollToIndex(i);
        }
      }

      isDragging = false;
      updateDisabled();
    };

    track.addEventListener("pointerup", endDrag);
    track.addEventListener("pointercancel", endDrag);

    // Wheel / trackpad support:
    // - Keep normal vertical page scrolling when the pointer is over a carousel.
    // - Only capture intentional horizontal gestures (or Shift+wheel).
    let wheelSnapTimer = null;
    track.addEventListener(
      "wheel",
      (e) => {
        if (track.scrollWidth <= track.clientWidth + 1) return;

        const dx = e.deltaX || 0;
        const dy = e.deltaY || 0;
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);
        const horizontalIntent = e.shiftKey || absDx > absDy * 1.15;
        if (!horizontalIntent) return;

        // Shift+wheel usually emits vertical delta; map it to horizontal.
        const hDelta = e.shiftKey && absDx < 0.01 ? dy : dx;
        if (hDelta === 0) return;

        track.scrollLeft += hDelta;
        e.preventDefault();

        if (wheelSnapTimer) clearTimeout(wheelSnapTimer);
        wheelSnapTimer = setTimeout(() => {
          const offsets = getOffsets();
          if (!offsets.length) return;
          const i = nearestIndex(track.scrollLeft, offsets);
          scrollToIndex(i);
        }, 120);
      },
      { passive: false },
    );

    track.addEventListener("scroll", updateDisabled, { passive: true });
    window.addEventListener("resize", updateDisabled);

    track.scrollLeft = 0;
    updateDisabled();
  };

  qsa("[data-carousel-wrap]").forEach(initCarouselWrap);

  // ---------------------------
  // Hero right-card carousel: directional slide transitions with dots + arrows
  // ---------------------------
  const initHeroCarousel = (root) => {
    const slides = qsa("[data-nei-hero-slide]", root);
    if (!slides.length) return;

    const labels = qsa("[data-nei-hero-label]", root.parentElement || root);
    const dots = qsa("[data-nei-hero-dot]", root.parentElement || root);
    const nav = qsa("[data-nei-hero-nav]", root);
    const autoplayEnabled =
      (root.getAttribute("data-nei-hero-autoplay") || "false") === "true";
    const autoplayMsRaw = parseInt(
      root.getAttribute("data-nei-hero-autoplay-ms") || "0",
      10,
    );
    const autoplayMs = Number.isFinite(autoplayMsRaw) ? autoplayMsRaw : 0;

    let index = slides.findIndex((slide) => slide.classList.contains("is-active"));
    if (index < 0) index = 0;
    let autoplayTimer = null;
    let animTimer = null;
    const motionMs = 460;
    const motionClasses = [
      "is-pre-enter-left",
      "is-pre-enter-right",
      "is-entering",
      "is-exit-left",
      "is-exit-right",
    ];

    const normalize = (i) => {
      const total = slides.length;
      if (!total) return 0;
      return ((i % total) + total) % total;
    };

    const clearMotionClasses = (slide) =>
      motionClasses.forEach((cn) => slide.classList.remove(cn));

    const updateIndicators = () => {
      labels.forEach((label, i) => {
        const on = i === index;
        label.classList.toggle("is-active", on);
        label.setAttribute("aria-hidden", on ? "false" : "true");
      });

      dots.forEach((dot, i) => {
        const on = i === index;
        dot.classList.toggle("is-active", on);
        dot.setAttribute("aria-current", on ? "true" : "false");
      });
    };

    const commitActive = (nextIndex) => {
      index = normalize(nextIndex);
      if (animTimer) {
        clearTimeout(animTimer);
        animTimer = null;
      }
      slides.forEach((slide, i) => {
        clearMotionClasses(slide);
        const on = i === index;
        slide.classList.toggle("is-active", on);
        slide.setAttribute("aria-hidden", on ? "false" : "true");
      });
      updateIndicators();
    };

    const setActive = (nextIndex, opts = {}) => {
      const direction = opts.direction === "left" ? "left" : "right";
      const next = normalize(nextIndex);

      // If a transition is already running, settle to a stable active slide first.
      if (animTimer) commitActive(index);

      if (next === index) {
        updateIndicators();
        return;
      }

      if (slides.length < 2) {
        commitActive(next);
        return;
      }

      const prev = index;
      const prevSlide = slides[prev];
      const nextSlide = slides[next];
      if (!prevSlide || !nextSlide) {
        commitActive(next);
        return;
      }

      index = next;
      updateIndicators();

      const enterClass =
        direction === "left" ? "is-pre-enter-left" : "is-pre-enter-right";
      const exitClass =
        direction === "left" ? "is-exit-right" : "is-exit-left";

      slides.forEach((slide) => clearMotionClasses(slide));
      prevSlide.classList.add("is-active");
      prevSlide.setAttribute("aria-hidden", "false");
      nextSlide.classList.remove("is-active");
      nextSlide.classList.add(enterClass);
      nextSlide.setAttribute("aria-hidden", "false");

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          nextSlide.classList.remove("is-pre-enter-left", "is-pre-enter-right");
          nextSlide.classList.add("is-entering");
          prevSlide.classList.remove("is-active");
          prevSlide.classList.add(exitClass);
        });
      });

      animTimer = setTimeout(() => {
        commitActive(index);
      }, motionMs);
    };

    const stopAutoplay = () => {
      if (!autoplayTimer) return;
      clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    const startAutoplay = () => {
      if (!autoplayEnabled || slides.length < 2 || autoplayMs < 1000) return;
      stopAutoplay();
      autoplayTimer = setInterval(() => {
        setActive(index + 1, { direction: "right" });
      }, autoplayMs);
    };

    const restartAutoplay = () => {
      if (!autoplayEnabled || slides.length < 2) return;
      startAutoplay();
    };

    nav.forEach((btn) =>
      btn.addEventListener("click", () => {
        const dir = parseInt(btn.getAttribute("data-nei-hero-nav") || "0", 10) || 0;
        if (!dir) return;
        setActive(index + dir, { direction: dir < 0 ? "left" : "right" });
        restartAutoplay();
      }),
    );

    dots.forEach((dot, i) =>
      dot.addEventListener("click", () => {
        setActive(i, { direction: i < index ? "left" : "right" });
        restartAutoplay();
      }),
    );

    root.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setActive(index - 1, { direction: "left" });
        restartAutoplay();
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        setActive(index + 1, { direction: "right" });
        restartAutoplay();
      }
    });

    root.addEventListener("mouseenter", stopAutoplay);
    root.addEventListener("mouseleave", startAutoplay);
    root.addEventListener("focusin", stopAutoplay);
    root.addEventListener("focusout", (e) => {
      const next = e.relatedTarget;
      if (next && root.contains(next)) return;
      startAutoplay();
    });
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });

    document.addEventListener("shopify:block:select", (e) => {
      const selectedSlide = e.target.closest("[data-nei-hero-slide]");
      if (!selectedSlide || !root.contains(selectedSlide)) return;
      const selectedIndex = slides.indexOf(selectedSlide);
      if (selectedIndex < 0) return;
      setActive(selectedIndex, {
        direction: selectedIndex < index ? "left" : "right",
      });
      restartAutoplay();
    });

    commitActive(index);
    startAutoplay();
  };

  qsa("[data-nei-hero-carousel]").forEach(initHeroCarousel);

  // ---------------------------
  // Live search (predictive) + desktop account popover
  // ---------------------------
  const debounce = (fn, wait = 150) => {
    let t = null;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const fmtCents = (cents) => {
    const code =
      window.Shopify && Shopify.currency && Shopify.currency.active
        ? Shopify.currency.active
        : "USD";
    const n = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: code,
      }).format(n);
    } catch {
      return `$${n.toFixed(2)}`;
    }
  };

  // Global search backdrop (dims the page behind the sticky header when search is focused/open)
  const searchBackdrop = document.getElementById("neiSearchBackdrop");
  let activeSearchWrap = null;

  const setBackdrop = (on, wrap) => {
    if (!searchBackdrop) return;
    if (on) {
      document.body.classList.add("nei-search-dim-open");
      activeSearchWrap = wrap || activeSearchWrap;
    } else {
      document.body.classList.remove("nei-search-dim-open");
      activeSearchWrap = null;
    }
  };

  const hideModal = (wrap) => {
    const modal = qs("[data-live-search-modal]", wrap);
    if (modal) modal.hidden = true;
    if (activeSearchWrap === wrap) setBackdrop(false);
  };

  const showModal = (wrap) => {
    const modal = qs("[data-live-search-modal]", wrap);
    if (modal) modal.hidden = false;
    setBackdrop(true, wrap);
  };

  const renderLoading = (resultsEl) => {
    resultsEl.innerHTML = `<div class="nei-search-loading">Searching…</div>`;
  };

  const renderHint = (resultsEl) => {
    resultsEl.innerHTML = `<div class="nei-search-hint">Start typing to see results…</div>`;
  };

  const renderResults = (resultsEl, q, data, opts = {}) => {
    const desktopPanel = !!opts.desktopPanel;
    const hiddenProductTag = "addon-only";
    const hasHiddenTag = (product) => {
      if (!product) return false;
      if (Array.isArray(product.tags)) {
        return product.tags.some(
          (tag) => String(tag || "").trim().toLowerCase() === hiddenProductTag,
        );
      }
      if (typeof product.tags === "string") {
        return product.tags
          .split(",")
          .some((tag) => String(tag || "").trim().toLowerCase() === hiddenProductTag);
      }
      return false;
    };
    const products = (data?.resources?.results?.products || []).filter(
      (product) => !hasHiddenTag(product),
    );
    const collections = data?.resources?.results?.collections || [];

    const esc = (s) =>
      String(s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    const productRows = products
      .map((p) => {
        const title = esc(p.title);
        const url = esc(p.url);
        const img = p.featured_image?.url || p.image || p.featured_image || "";
        const imgTag = img ? `<img src="${esc(img)}" alt="${title}">` : "";
        const vendor = p.vendor ? esc(p.vendor) : "";
        const vendorLine = vendor
          ? `<div class="nei-search-item-vendor">${vendor}</div>`
          : "";
        const price = p.price != null ? fmtCents(p.price) : "";
        const meta = price
          ? `<div class="nei-search-item-meta">${price}</div>`
          : "";
        return `
        <a class="nei-search-item" href="${url}">
          <span class="nei-search-item-thumb">${imgTag}</span>
          <span>
            ${vendorLine}
            <div class="nei-search-item-title">${title}</div>
            ${meta}
          </span>
        </a>
      `.trim();
      })
      .join("");

    const collectionRows = collections
      .map((c) => {
        const title = esc(c.title);
        const url = esc(c.url);
        return `
        <a class="nei-search-item" href="${url}">
          <span class="nei-search-item-thumb">#</span>
          <span>
            <div class="nei-search-item-title">${title}</div>
            <div class="nei-search-item-meta">Collection</div>
          </span>
        </a>
      `.trim();
      })
      .join("");

    const viewAllUrl = `/search?q=${encodeURIComponent(q)}`;

    if (!products.length && !collections.length) {
      resultsEl.innerHTML = `
        <div class="nei-search-hint">No results for <strong>${esc(q)}</strong>.</div>
        <div class="nei-search-viewall"><a href="${viewAllUrl}">View all results</a></div>
      `.trim();
      return;
    }

    let html = "";
    if (products.length) {
      html += `
        <div class="nei-search-section">
          ${desktopPanel ? "" : '<div class="nei-search-section-title">Products</div>'}
          ${productRows}
        </div>
      `;
    }
    if (collections.length) {
      html += `
        <div class="nei-search-section">
          <div class="nei-search-section-title">Collections</div>
          ${collectionRows}
        </div>
      `;
    }

    html += `<div class="nei-search-viewall"><a href="${viewAllUrl}">View all results</a></div>`;
    resultsEl.innerHTML = html;
  };

  const initLiveSearch = () => {
    const wraps = qsa("[data-live-search-wrap]");
    if (!wraps.length) return;

    // Clicking the dimmed backdrop should close the active search UI (and collapse the search row if applicable)
    if (searchBackdrop && !searchBackdrop.dataset.bound) {
      searchBackdrop.dataset.bound = "1";
      searchBackdrop.addEventListener("click", () => {
        if (!activeSearchWrap) return;
        const parentCollapse = activeSearchWrap.closest(".collapse");
        if (parentCollapse && parentCollapse.classList.contains("show")) {
          try {
            bootstrap.Collapse.getOrCreateInstance(parentCollapse, {
              toggle: false,
            }).hide();
          } catch {}
        }
        const input = qs("[data-live-search-input]", activeSearchWrap);
        try {
          input && input.blur && input.blur();
        } catch {}
        hideModal(activeSearchWrap);
      });
    }

    const RECENT_KEY = "nei_recent_searches_v1";
    const loadRecent = () => {
      try {
        const v = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
        return Array.isArray(v) ? v : [];
      } catch {
        return [];
      }
    };
    const saveRecent = (arr) => {
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(arr));
      } catch {}
    };

    const esc = (s) =>
      String(s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    wraps.forEach((wrap) => {
      const input = qs("[data-live-search-input]", wrap);
      const resultsEl = qs("[data-live-search-results]", wrap);
      const modal = qs("[data-live-search-modal]", wrap);
      const form = qs("[data-live-search-form]", wrap);

      // Mobile initial-state panel (recent + popular searches)
      const mobileInitialEl = qs("[data-live-search-initial]", wrap);

      // Desktop-only initial-state elements
      const recentEl = qs("[data-recent-searches]", wrap);
      const popularProductsEl = qs("[data-popular-products]", wrap);
      const rightTitleEl = qs("[data-search-right-title]", wrap);
      const isDesktopPanel = !!(recentEl && popularProductsEl && rightTitleEl);

      if (!input || !resultsEl) return;

      let controller = null;

      const renderRecent = () => {
        if (!recentEl) return;
        const arr = loadRecent();
        if (!arr.length) {
          recentEl.innerHTML = `<div class="nei-search-hint">No recent searches yet.</div>`;
          return;
        }
        recentEl.innerHTML = arr
          .map(
            (term) =>
              `<button type="button" class="nei-search-recent-link" data-recent-term="${esc(term)}">${esc(term)}</button>`,
          )
          .join("");
      };

      const addRecent = (term) => {
        const t = (term || "").trim();
        if (!t) return;
        let arr = loadRecent();
        const low = t.toLowerCase();
        arr = arr.filter((x) => String(x || "").toLowerCase() !== low);
        arr.unshift(t);
        arr = arr.slice(0, 8);
        saveRecent(arr);
        renderRecent();
      };

      const setModeInitial = () => {
        if (isDesktopPanel) {
          rightTitleEl.textContent = "Popular Products";
          if (popularProductsEl) popularProductsEl.hidden = false;
          resultsEl.hidden = true;
          renderRecent();
          return;
        }

        // Mobile: show initial panel (recent + popular searches) when empty
        if (mobileInitialEl) {
          mobileInitialEl.hidden = false;
          resultsEl.hidden = true;
          renderRecent();
          return;
        }

        // Fallback: simple hint
        resultsEl.hidden = false;
        renderHint(resultsEl);
      };

      const setModeResults = () => {
        if (isDesktopPanel) {
          rightTitleEl.textContent = "Products";
          if (popularProductsEl) popularProductsEl.hidden = true;
        }
        if (mobileInitialEl) mobileInitialEl.hidden = true;
        resultsEl.hidden = false;
      };

      const performSearch = async (q) => {
        showModal(wrap);
        setModeResults();
        renderLoading(resultsEl);

        // cancel previous
        try {
          controller?.abort();
        } catch {}
        controller = new AbortController();

        const filteredQuery = `${q} -tag:addon-only`;
        const url = `/search/suggest.json?q=${encodeURIComponent(filteredQuery)}&resources[type]=product,collection&resources[limit]=6&resources[options][unavailable_products]=hide`;

        try {
          const res = await fetch(url, {
            headers: { Accept: "application/json" },
            credentials: "same-origin",
            signal: controller.signal,
          });
          if (!res.ok) throw new Error("Search failed");
          const data = await res.json();
          renderResults(resultsEl, q, data, { desktopPanel: isDesktopPanel });
        } catch (e) {
          if (e.name === "AbortError") return;
          resultsEl.innerHTML = `<div class="nei-search-hint">Couldn’t load results. <a href="/search?q=${encodeURIComponent(q)}">Search instead</a></div>`;
        }
      };

      const doSearch = debounce(() => {
        const q = (input.value || "").trim();
        if (!q) {
          // Initial state (desktop) or hint (mobile)
          setModeInitial();
          showModal(wrap);
          return;
        }
        performSearch(q);
      }, 160);

      input.addEventListener("input", doSearch);
      input.addEventListener("focus", () => {
        const q = (input.value || "").trim();
        showModal(wrap);
        if (!q) setModeInitial();
        else doSearch();
      });

      // Save term on submit (for desktop + mobile)
      if (form) {
        form.addEventListener("submit", () => {
          const q = (input.value || "").trim();
          if (q) addRecent(q);
        });
      }

      // Clicking recent-search items fills input and triggers live results
      if (recentEl) {
        recentEl.addEventListener("click", (e) => {
          const btn = e.target.closest("[data-recent-term]");
          if (!btn) return;
          e.preventDefault();
          const term = btn.getAttribute("data-recent-term") || "";
          input.value = term;
          input.focus();
          input.dispatchEvent(new Event("input", { bubbles: true }));
        });
      }

      // Close when clicking outside
      document.addEventListener("click", (e) => {
        if (!wrap.contains(e.target)) hideModal(wrap);
      });

      // Close on ESC
      document.addEventListener("keydown", (e) => {
        if (e.key !== "Escape") return;
        hideModal(wrap);
      });

      // If search field is collapsed/hidden by Bootstrap, close modal.
      const parentCollapse = wrap.closest(".collapse");
      if (parentCollapse) {
        parentCollapse.addEventListener("hidden.bs.collapse", () =>
          hideModal(wrap),
        );
      }

      // Initial render
      if (isDesktopPanel) setModeInitial();
      else renderHint(resultsEl);
    });
  };

  const initDesktopAccountPopover = () => {
    const media = window.matchMedia("(min-width: 992px)");
    const wrap = qs(".nei-account-wrap");
    const trigger = wrap ? qs("[data-account-trigger]", wrap) : null;
    const pop = wrap ? qs(".nei-account-popover", wrap) : null;
    if (!wrap || !trigger || !pop) return; // logged-in or not present

    let open = false;
    const setOpen = (v) => {
      open = v;
      pop.style.display = open ? "block" : "";
    };

    trigger.addEventListener("click", (e) => {
      if (!media.matches) return; // mobile should navigate
      e.preventDefault();
      setOpen(!open);
    });

    document.addEventListener("click", (e) => {
      if (!media.matches) return;
      if (wrap.contains(e.target)) return;
      if (open) setOpen(false);
    });

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      if (open) setOpen(false);
    });

    media.addEventListener?.("change", () => setOpen(false));
  };

  initLiveSearch();
  initDesktopAccountPopover();

  // ---------------------------
  // Cart drawer (AJAX cart)
  // ---------------------------
  const drawer = document.getElementById("cartDrawer");
  if (!drawer) return;

  const settingsEl = document.getElementById("neiSettings");
  const freeShipThreshold = settingsEl
    ? parseFloat(settingsEl.dataset.freeShippingThreshold || "0") || 0
    : 0;
  const badgeShipsSeparately = settingsEl
    ? settingsEl.dataset.cartBadgeShipsSeparately || "Ships separately"
    : "Ships separately";
  const badgeNoFreeShipping = settingsEl
    ? settingsEl.dataset.cartBadgeNoFreeShipping || "No free shipping"
    : "No free shipping";

  let lastFocus = null;

  const fmtMoney = (cents) => {
    // Shopify returns cents. Use Intl with shop currency code if available.
    const code =
      window.Shopify && Shopify.currency && Shopify.currency.active
        ? Shopify.currency.active
        : "USD";
    const n = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: code,
      }).format(n);
    } catch {
      return `$${n.toFixed(2)}`;
    }
  };

  const api = async (url, body) => {
    const res = await fetch(url, {
      method: body ? "POST" : "GET",
      headers: body
        ? { "Content-Type": "application/json", Accept: "application/json" }
        : { Accept: "application/json" },
      body: body ? JSON.stringify(body) : undefined,
      credentials: "same-origin",
    });
    if (!res.ok) {
      let msg = "Request failed";
      try {
        msg = (await res.text()) || msg;
      } catch {}
      throw new Error(msg);
    }
    return res.json();
  };

  const updateFreeShipping = (subtotalCents) => {
    const msgEl = qs("[data-free-ship-msg]", drawer);
    const remEl = qs("[data-free-ship-remaining]", drawer);
    const fillEl = qs("[data-free-ship-fill]", drawer);
    const barEl = qs(".free-ship-bar", drawer);
    if (!msgEl || !remEl || !fillEl || !barEl || !freeShipThreshold) return;

    const thresholdCents = Math.round(freeShipThreshold * 100);
    const remaining = Math.max(0, thresholdCents - subtotalCents);
    const pct = Math.max(
      0,
      Math.min(100, (subtotalCents / thresholdCents) * 100),
    );

    fillEl.style.width = `${pct.toFixed(1)}%`;
    barEl.setAttribute("aria-valuemax", String(freeShipThreshold));
    barEl.setAttribute(
      "aria-valuenow",
      String(
        Math.min(
          freeShipThreshold,
          Math.round((subtotalCents / 100) * 100) / 100,
        ),
      ),
    );

    if (remaining > 0) {
      msgEl.textContent = `Add ${fmtMoney(remaining)} more to unlock free shipping`;
      remEl.textContent = `${fmtMoney(remaining)} left`;
      remEl.classList.remove("is-unlocked");
    } else {
      msgEl.textContent = "Free shipping unlocked!";
      remEl.textContent = "Unlocked";
      remEl.classList.add("is-unlocked");
    }
  };

  // Free-shipping eligibility info (eligible subtotal + excluded lines)
  // Excludes items tagged with settings.free_shipping_exclude_tag.
  const fetchEligibleInfo = async () => {
    try {
      const res = await fetch(`/?sections=cart-drawer&_=${Date.now()}`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return null;
      const data = await res.json();
      const html = data && data["cart-drawer"];
      if (!html) return null;
      const doc = new DOMParser().parseFromString(html, "text/html");
      const el = doc.querySelector("[data-eligible-subtotal]");
      if (!el) return null;
      const cents = parseInt(
        el.getAttribute("data-eligible-subtotal") || "0",
        10,
      );
      const linesWrap = doc.querySelector("[data-eligible-lines]");
      const excludedLines = new Set();
      if (linesWrap) {
        linesWrap
          .querySelectorAll("[data-line][data-excluded]")
          .forEach((n) => {
            const line = parseInt(n.getAttribute("data-line") || "0", 10);
            const excluded =
              (n.getAttribute("data-excluded") || "").toLowerCase() === "true";
            if (line && excluded) excludedLines.add(line);
          });
      }
      return Number.isFinite(cents)
        ? { eligibleCents: cents, excludedLines }
        : null;
    } catch (e) {
      return null;
    }
  };

  const setCounts = (count) => {
    qsa("[data-cart-count]").forEach(
      (el) => (el.textContent = String(count || 0)),
    );
    qsa(".icon-badge[data-cart-count]").forEach((el) => {
      el.style.display = (count || 0) > 0 ? "grid" : "none";
    });
  };

  const getItemProps = (item) =>
    item && item.properties && typeof item.properties === "object"
      ? item.properties
      : {};

  const getPropValueCI = (props, propName) => {
    const target = String(propName || "").trim().toLowerCase();
    if (!target) return "";
    for (const [k, v] of Object.entries(props || {})) {
      if (String(k || "").trim().toLowerCase() !== target) continue;
      return String(v == null ? "" : v).trim();
    }
    return "";
  };

  const getExtraGroupFromProps = (props) => {
    const keys = [
      "_nei_extra_group",
      "_nei_extras_group",
      "_NEI Extra Group",
      "Panel Engraving ID",
    ];
    for (const k of keys) {
      const value = String(props?.[k] == null ? "" : props[k]).trim();
      if (value) return value;
    }
    return "";
  };

  const getExtraGroupFromItem = (item) => getExtraGroupFromProps(getItemProps(item));

  const isLegacyExtraLineItem = (item) => {
    const props = getItemProps(item);
    return !!(
      getPropValueCI(props, "Custom Engraving") || getPropValueCI(props, "Engraving For")
    );
  };

  const getLinkedRemoveLines = (cart, selectedLine) => {
    const lines = new Set();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const selectedIndex = selectedLine - 1;
    if (selectedIndex < 0 || selectedIndex >= items.length) return lines;

    lines.add(selectedLine);
    const selectedItem = items[selectedIndex];
    const selectedProps = getItemProps(selectedItem);
    const selectedGroup = getExtraGroupFromProps(selectedProps);

    if (selectedGroup) {
      items.forEach((cartItem, idx) => {
        if (idx === selectedIndex) return;
        if (getExtraGroupFromItem(cartItem) === selectedGroup) lines.add(idx + 1);
      });
      return lines;
    }

    // Legacy fallback for existing engraving lines added before group keys existed.
    const engravingFor = getPropValueCI(selectedProps, "Engraving For");
    const customEngraving = getPropValueCI(selectedProps, "Custom Engraving");

    if (engravingFor) {
      const targetTitle = engravingFor.toLowerCase();
      items.forEach((cartItem, idx) => {
        if (idx === selectedIndex) return;
        const props = getItemProps(cartItem);
        if (!getPropValueCI(props, "Custom Engraving")) return;
        const cartTitle = String(cartItem.product_title || cartItem.title || "")
          .trim()
          .toLowerCase();
        if (!targetTitle || cartTitle === targetTitle) lines.add(idx + 1);
      });
      return lines;
    }

    if (customEngraving) {
      const selectedTitle = String(selectedItem.product_title || selectedItem.title || "")
        .trim()
        .toLowerCase();
      items.forEach((cartItem, idx) => {
        if (idx === selectedIndex) return;
        const props = getItemProps(cartItem);
        const forTitle = getPropValueCI(props, "Engraving For").toLowerCase();
        if (!forTitle) return;
        if (!selectedTitle || forTitle === selectedTitle) lines.add(idx + 1);
      });
    }

    return lines;
  };
  const renderCart = (cart, eligibleInfo) => {
    const itemsWrap = qs("[data-cart-items]", drawer);
    const subtotalEl = qs("[data-cart-subtotal]", drawer);
    if (!itemsWrap) return;

    const escHtml = (value) =>
      String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#39;");
    const escAttr = (value) => escHtml(value).replace(/`/g, "&#96;");

    itemsWrap.innerHTML = "";

    (cart.items || []).forEach((item, idx) => {
      const line = document.createElement("div");
      line.className = "cart-item";
      line.dataset.line = String(idx + 1);

      const isExcluded = !!(
        eligibleInfo &&
        eligibleInfo.excludedLines &&
        eligibleInfo.excludedLines.has(idx + 1)
      );

      const img = item.image
        ? `<img src="${escAttr(item.image)}" alt="${escAttr(item.product_title || "")}">`
        : "";
      const optionText = (item.options_with_values || [])
        .map((o) => `${o.name}: ${o.value}`)
        .join(" · ");
      const itemProps = getItemProps(item);
      const variantMeta =
        optionText ||
        (item.variant_title && item.variant_title !== "Default Title"
          ? item.variant_title
          : "");
      const propertyMeta = Object.entries(itemProps)
        .filter(([k, v]) => {
          const key = String(k || "");
          const val = String(v == null ? "" : v).trim();
          return !!key && key.charAt(0) !== "_" && !!val;
        })
        .map(([k, v]) => {
          const key = escHtml(k);
          const val = String(v == null ? "" : v).trim();
          const isUrl = /^https?:\/\//i.test(val) || val.startsWith("/uploads/");
          const valueHtml = isUrl
            ? `<a href="${escAttr(val)}" target="_blank" rel="noopener">View file</a>`
            : escHtml(val);
          return `<div><span>${key}:</span> ${valueHtml}</div>`;
        })
        .join("");
      const meta = `${variantMeta ? `<div>${escHtml(variantMeta)}</div>` : ""}${propertyMeta}`;
      const extraGroup = getExtraGroupFromProps(itemProps);
      const lockQty = !!extraGroup || isLegacyExtraLineItem(item);
      line.dataset.extraGroup = extraGroup || "";
      line.dataset.lockQty = lockQty ? "true" : "false";
      const qtyControlsHtml = lockQty
        ? `
          <div class="qty" data-qty-locked>
            <input class="qty-input" type="number" min="1" step="1" value="${item.quantity}" inputmode="numeric" data-qty-input aria-label="Quantity" readonly aria-readonly="true">
          </div>
        `.trim()
        : `
          <div class="qty" data-qty>
            <button class="qty-btn" type="button" data-qty-step="-1" aria-label="Decrease quantity">-</button>
            <input class="qty-input" type="number" min="1" step="1" value="${item.quantity}" inputmode="numeric" data-qty-input aria-label="Quantity">
            <button class="qty-btn" type="button" data-qty-step="1" aria-label="Increase quantity">+</button>
          </div>
        `.trim();

      const badge = isExcluded
        ? `
          <div class="cart-item-badges" aria-label="Shipping eligibility">
            <div class="cart-item-badge" title="${badgeShipsSeparately}">${badgeShipsSeparately}</div>
            <div class="cart-item-badge cart-item-badge--muted" title="Doesn't count toward free shipping">No free shipping</div>
          </div>
        `.trim()
        : "";

      line.innerHTML = `
        <div class="cart-thumb" aria-hidden="true">${img}</div>
        <div class="cart-item-main">
          <div class="cart-item-top">
            <a class="cart-item-name" href="${escAttr(item.url || "#")}">${escHtml(item.product_title || item.title)}</a>
            <button class="cart-remove" type="button" data-cart-remove aria-label="Remove item">${qs("[data-icon-trash-template]", drawer)?.innerHTML || "x"}</button>
          </div>
          <div class="cart-item-meta">${meta || ""}</div>
          ${badge}
          <div class="cart-item-bottom">
            ${qtyControlsHtml}
            <div class="cart-price" data-cart-line-total>${fmtMoney(item.final_line_price)}</div>
          </div>
        </div>
      `.trim();

      itemsWrap.appendChild(line);
    });

    if (subtotalEl) subtotalEl.textContent = fmtMoney(cart.total_price || 0);
    setCounts(cart.item_count || 0);
    // Free-shipping progress uses an eligible subtotal (excludes tagged collaborator items).
    // Fallback to full cart total if eligibility info isn't available.
    const eligibleSubtotal =
      eligibleInfo && typeof eligibleInfo.eligibleCents === "number"
        ? eligibleInfo.eligibleCents
        : cart.total_price || 0;
    updateFreeShipping(eligibleSubtotal);

    // Checkout button disable when empty
    const checkoutBtn = qs("[data-checkout-btn]", drawer);
    if (checkoutBtn) checkoutBtn.disabled = (cart.item_count || 0) === 0;
  };

  const refreshCart = async () => {
    const [cart, eligibleInfo] = await Promise.all([
      api("/cart.js"),
      fetchEligibleInfo(),
    ]);
    renderCart(cart, eligibleInfo);
    return cart;
  };

  const openCart = async (openerEl) => {
    // Store an explicit opener when possible so closing the drawer doesn't
    // refocus a deep page element (which can scroll the page).
    lastFocus = openerEl || document.activeElement;
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    document.body.classList.add("cart-open");
    try {
      await refreshCart();
    } catch (e) {
      console.warn(e);
    }
    setTimeout(() => qs(".cart-close-btn", drawer)?.focus(), 0);
  };

  // Expose minimal cart API for other scripts (e.g., product page)
  window.neiCart = window.neiCart || {};
  window.neiCart.open = openCart;
  window.neiCart.refresh = refreshCart;
  window.neiCart.api = api;

  const safeRestoreFocus = (el) => {
    if (!el || typeof el.focus !== "function") return;
    const sx = window.scrollX || window.pageXOffset || 0;
    const sy = window.scrollY || window.pageYOffset || 0;
    try {
      // Modern browsers: avoid jumping the page when restoring focus.
      el.focus({ preventScroll: true });
    } catch (_) {
      // Fallback: focus normally, then restore scroll position.
      el.focus();
    }
    // Some browsers still scroll even with preventScroll on certain elements.
    // Restore scroll on the next couple frames to reliably cancel any focus-induced jump.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          window.scrollTo(sx, sy);
        } catch (_) {}
      });
    });
    setTimeout(() => {
      try {
        window.scrollTo(sx, sy);
      } catch (_) {}
    }, 50);
  };

  const closeCart = () => {
    const sx = window.scrollX || window.pageXOffset || 0;
    const sy = window.scrollY || window.pageYOffset || 0;
    drawer.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    document.body.classList.remove("cart-open");
    setTimeout(() => {
      // Restoring focus to a deep page element (e.g., an Add-to-cart button)
      // can cause the browser to scroll to it. Prefer restoring to the header
      // cart button unless the opener was already in the header.
      const headerCartBtn = qs("[data-cart-open]");
      const focusTarget =
        lastFocus &&
        (lastFocus.closest("header") ||
          lastFocus.closest("[data-site-header]") ||
          lastFocus.matches?.("[data-cart-open]"))
          ? lastFocus
          : headerCartBtn;
      safeRestoreFocus(focusTarget);
      // Extra guard: ensure we never jump after closing.
      requestAnimationFrame(() => {
        try {
          window.scrollTo(sx, sy);
        } catch (_) {}
      });
      lastFocus = null;
    }, 0);
  };

  // Global open/close handlers
  document.addEventListener("click", async (e) => {
    const openBtn = e.target.closest("[data-cart-open]");
    if (openBtn) {
      e.preventDefault();
      if (!drawer.classList.contains("is-open")) await openCart(openBtn);
      return;
    }

    const closeBtn = e.target.closest("[data-cart-close]");
    if (closeBtn && drawer.classList.contains("is-open")) {
      e.preventDefault();
      closeCart();
      return;
    }

    // Remove line
    const removeBtn = e.target.closest("[data-cart-remove]");
    if (removeBtn) {
      e.preventDefault();
      const item = removeBtn.closest(".cart-item");
      const line = parseInt(item?.dataset.line || "0", 10);
      if (!line) return;
      try {
        const cart = await api("/cart.js");
        const linkedLines = getLinkedRemoveLines(cart, line);
        if (!linkedLines.size) linkedLines.add(line);
        const updates = (cart.items || []).map((cartItem, idx) =>
          linkedLines.has(idx + 1) ? 0 : cartItem.quantity,
        );
        await api("/cart/update.js", { updates });
        await refreshCart();
      } catch (err) {
        console.warn(err);
      }
      return;
    }

    // Qty step
    const stepBtn = e.target.closest("[data-qty-step]");
    if (stepBtn) {
      e.preventDefault();
      const item = stepBtn.closest(".cart-item");
      const line = parseInt(item?.dataset.line || "0", 10);
      if (!line) return;

      const input = qs("[data-qty-input]", item);
      const cur = parseInt(input?.value || "1", 10) || 1;
      const step = parseInt(stepBtn.dataset.qtyStep || "0", 10) || 0;
      const next = Math.max(1, cur + step);
      if (input) input.value = String(next);

      try {
        await api("/cart/change.js", { line, quantity: next });
        await refreshCart();
      } catch (err) {
        console.warn(err);
      }
      return;
    }

    // Add recommended product (uses variant id)
    const addReco = e.target.closest("[data-cart-add-reco]");
    if (addReco) {
      e.preventDefault();
      const id = parseInt(addReco.dataset.variantId || "0", 10);
      if (!id) return;
      try {
        await api("/cart/add.js", { items: [{ id, quantity: 1 }] });
        await refreshCart();
      } catch (err) {
        console.warn(err);
      }
      return;
    }
  });

  // Qty input direct change
  drawer.addEventListener("change", async (e) => {
    const input = e.target.closest("[data-qty-input]");
    if (!input) return;
    const item = input.closest(".cart-item");
    const line = parseInt(item?.dataset.line || "0", 10);
    if (!line) return;
    if ((item?.dataset.lockQty || "").toLowerCase() === "true") {
      input.value = String(parseInt(input.defaultValue || "1", 10) || 1);
      return;
    }

    let qty = parseInt(input.value || "1", 10);
    if (!Number.isFinite(qty) || qty < 1) qty = 1;
    input.value = String(qty);

    try {
      await api("/cart/change.js", { line, quantity: qty });
      await refreshCart();
    } catch (err) {
      console.warn(err);
    }
  });

  // Esc closes
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (!drawer.classList.contains("is-open")) return;
    closeCart();
  });

  // Intercept add-to-cart forms and open drawer
  document.addEventListener("submit", async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = (form.getAttribute("action") || "").toLowerCase();
    if (!action.includes("/cart/add")) return;

    // If merchant disabled cart drawer, let Shopify navigate normally
    const enableDrawer = settingsEl
      ? settingsEl.dataset.enableCartDrawer === "true"
      : true;
    if (!enableDrawer) return;

    e.preventDefault();
    const fd = new FormData(form);

    // Ensure quantity present
    if (!fd.get("quantity")) fd.set("quantity", "1");

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        body: fd,
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(await res.text());
      await openCart(qs("[data-cart-open]"));
    } catch (err) {
      console.warn(err);
      // Fallback: navigate to cart
      window.location.href = "/cart";
    }
  });

  // Keep header badge in sync on first load
  refreshCart().catch(() => {});
})();

/* ========================================
 * 1b) Main cart page linked-extra behavior
 * ====================================== */
(() => {
  const root = document.querySelector("[data-nei-main-cart]");
  if (!root) return;

  const api = async (url, body) => {
    const res = await fetch(url, {
      method: body ? "POST" : "GET",
      headers: body
        ? { "Content-Type": "application/json", Accept: "application/json" }
        : { Accept: "application/json" },
      body: body ? JSON.stringify(body) : undefined,
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  };

  const getProps = (item) =>
    item && item.properties && typeof item.properties === "object"
      ? item.properties
      : {};

  const getPropValueCI = (props, propName) => {
    const target = String(propName || "").trim().toLowerCase();
    if (!target) return "";
    for (const [k, v] of Object.entries(props || {})) {
      if (String(k || "").trim().toLowerCase() !== target) continue;
      return String(v == null ? "" : v).trim();
    }
    return "";
  };

  const getExtraGroup = (props) => {
    const keys = [
      "_nei_extra_group",
      "_nei_extras_group",
      "_NEI Extra Group",
      "Panel Engraving ID",
    ];
    for (const key of keys) {
      const value = String(props?.[key] == null ? "" : props[key]).trim();
      if (value) return value;
    }
    return "";
  };

  const getLinkedRemoveLines = (cart, selectedLine) => {
    const lines = new Set();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const selectedIndex = selectedLine - 1;
    if (selectedIndex < 0 || selectedIndex >= items.length) return lines;

    lines.add(selectedLine);
    const selectedItem = items[selectedIndex];
    const selectedProps = getProps(selectedItem);
    const selectedGroup = getExtraGroup(selectedProps);

    if (selectedGroup) {
      items.forEach((item, idx) => {
        if (idx === selectedIndex) return;
        if (getExtraGroup(getProps(item)) === selectedGroup) lines.add(idx + 1);
      });
      return lines;
    }

    const engravingFor = getPropValueCI(selectedProps, "Engraving For");
    const customEngraving = getPropValueCI(selectedProps, "Custom Engraving");

    if (engravingFor) {
      const targetTitle = engravingFor.toLowerCase();
      items.forEach((item, idx) => {
        if (idx === selectedIndex) return;
        const props = getProps(item);
        if (!getPropValueCI(props, "Custom Engraving")) return;
        const title = String(item.product_title || item.title || "")
          .trim()
          .toLowerCase();
        if (!targetTitle || title === targetTitle) lines.add(idx + 1);
      });
      return lines;
    }

    if (customEngraving) {
      const selectedTitle = String(selectedItem.product_title || selectedItem.title || "")
        .trim()
        .toLowerCase();
      items.forEach((item, idx) => {
        if (idx === selectedIndex) return;
        const props = getProps(item);
        const forTitle = getPropValueCI(props, "Engraving For").toLowerCase();
        if (!forTitle) return;
        if (!selectedTitle || forTitle === selectedTitle) lines.add(idx + 1);
      });
    }

    return lines;
  };

  root.addEventListener("click", async (e) => {
    const removeLink = e.target.closest("[data-cart-remove-line]");
    if (!removeLink) return;

    e.preventDefault();
    const line = parseInt(removeLink.getAttribute("data-cart-remove-line") || "0", 10);
    if (!line) return;

    try {
      const cart = await api("/cart.js");
      const linkedLines = getLinkedRemoveLines(cart, line);
      if (!linkedLines.size) linkedLines.add(line);
      const updates = (cart.items || []).map((item, idx) =>
        linkedLines.has(idx + 1) ? 0 : item.quantity,
      );
      await api("/cart/update.js", { updates });
      window.location.reload();
    } catch (err) {
      console.warn(err);
      window.location.href = removeLink.getAttribute("href") || "/cart";
    }
  });
})();

/* ========================================
 * 2) Product page behavior
 * ====================================== */
(() => {
  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => Array.from(r.querySelectorAll(s));

  const page = qs(".nei-product-page");
  if (!page) return;

  // ---------------------------
  // Gallery thumb -> main swap + fullscreen lightbox
  // ---------------------------
  const mainImg = qs("#neiProductMainImg");
  const thumbs = qsa(".nei-thumb", page);
  const thumbsTrack = qs(".nei-product-thumbs", page);
  const lightbox = qs("[data-nei-lightbox]", page);
  const lightboxImg = qs("[data-nei-lightbox-img]", page);
  const lightboxClose = qs("[data-nei-lightbox-close]", page);
  const thumbsDesktopMq = window.matchMedia("(min-width: 992px)");

  const isThumbCarouselActive = () =>
    !!(thumbsTrack && thumbs.length > 4 && thumbsDesktopMq.matches);

  const centerThumbInTrack = (thumbBtn, smooth = true) => {
    if (!isThumbCarouselActive() || !thumbBtn || !thumbsTrack) return;
    const trackH = thumbsTrack.clientHeight || 0;
    const maxScroll = Math.max(0, thumbsTrack.scrollHeight - trackH);
    const raw =
      (thumbBtn.offsetTop || 0) +
      (thumbBtn.offsetHeight || 0) / 2 -
      trackH / 2;
    const target = Math.max(0, Math.min(maxScroll, raw));
    thumbsTrack.scrollTo({ top: target, behavior: smooth ? "smooth" : "auto" });
  };

  const syncThumbCarouselMode = () => {
    if (!thumbsTrack) return;
    const active = isThumbCarouselActive();
    thumbsTrack.classList.toggle("is-carousel", active);
    if (active) {
      centerThumbInTrack(qs(".nei-thumb.is-active", page), false);
    } else {
      thumbsTrack.scrollTop = 0;
    }
  };

  const setMainImage = (url, altText = "") => {
    if (!mainImg || !url) return;
    mainImg.src = url;
    // image_tag can emit srcset/sizes; ensure swaps always reflect the clicked/selected media
    mainImg.removeAttribute("srcset");
    mainImg.removeAttribute("sizes");
    if (altText) mainImg.alt = altText;
    mainImg.dataset.neiCurrent = url;
  };

  const getActiveFull = () => {
    const active = qs(".nei-thumb.is-active", page);
    return active
      ? active.getAttribute("data-nei-full")
      : mainImg
        ? mainImg.dataset.neiCurrent || mainImg.currentSrc || mainImg.src
        : null;
  };

  thumbs.forEach((btn) => {
    btn.addEventListener("click", () => {
      const full = btn.getAttribute("data-nei-full");
      const alt = btn.getAttribute("data-nei-alt") || "";
      if (full) setMainImage(full, alt);
      thumbs.forEach((b) => b.classList.remove("is-active"));
      btn.classList.add("is-active");
      centerThumbInTrack(btn);
    });
  });

  thumbsDesktopMq.addEventListener?.("change", syncThumbCarouselMode);
  window.addEventListener("resize", syncThumbCarouselMode);
  syncThumbCarouselMode();

  const openLightbox = (src = "", altText = "") => {
    if (!lightbox || !lightboxImg) return;
    const full = src || getActiveFull();
    if (!full) return;
    lightboxImg.src = full;
    lightboxImg.alt = altText || (mainImg && mainImg.alt ? mainImg.alt : "");
    lightbox.hidden = false;
    document.documentElement.classList.add("nei-lock");
  };

  const closeLightbox = () => {
    if (!lightbox || !lightboxImg) return;
    lightbox.hidden = true;
    lightboxImg.src = "";
    document.documentElement.classList.remove("nei-lock");
  };

  if (mainImg) {
    mainImg.style.cursor = "zoom-in";
    mainImg.addEventListener("click", () => openLightbox());
  }
  if (lightboxClose) lightboxClose.addEventListener("click", closeLightbox);
  if (lightbox) {
    lightbox.addEventListener("click", (e) => {
      if (e.target === lightbox) closeLightbox();
    });
  }

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && lightbox && !lightbox.hidden) closeLightbox();
  });

  // ---------------------------
  // Variant selection from pills
  // ---------------------------
  const variantsJsonEl = qs("#neiProductVariantsJson");
  let variants = [];
  try {
    variants = variantsJsonEl
      ? JSON.parse(variantsJsonEl.textContent || "[]")
      : [];
  } catch {
    variants = [];
  }

  const form = qs(".nei-product-form", page);
  const variantIdInput = qs("[data-nei-variant-id]", page);
  // Variant dropdown removed; pills drive selection.
  const priceEl = qs("[data-nei-price]", page);
  const comparePriceEl = qs(".nei-buy-compare", page);
  const addBtn = qs("[data-nei-add-to-cart]", page);
  const qtyWrap = qs(".nei-buy-qty", page);
  const stockWrap = qs("[data-nei-stock]", page);
  const stockCountEl = qs("[data-nei-stock-count]", page);
  const lowStockEl = qs("[data-nei-low-stock]", page);
  let activeVariantPriceCents = 0;
  let activeVariantCompareAtCents = 0;
  let onVariantAvailabilityChanged = null;

  const fmtMoney = (cents) => {
    // Shopify injects a formatted string server-side; this is a fallback.
    const v = Number(cents || 0) / 100;
    return v.toLocaleString(undefined, {
      style: "currency",
      currency:
        (window.Shopify && Shopify.currency && Shopify.currency.active) ||
        "USD",
    });
  };

  const getSelectedOptions = () => {
    const optionWraps = qsa("[data-nei-option-index]", page);
    const opts = [];
    optionWraps.forEach((wrap) => {
      const active = qs(".nei-pill.is-active", wrap);
      opts.push(active ? active.getAttribute("data-nei-option-value") : null);
    });
    return opts;
  };

  const findVariant = (opts) => {
    if (!variants.length) return null;
    return (
      variants.find(
        (v) =>
          Array.isArray(v.options) &&
          v.options.every((o, i) => String(o) === String(opts[i])),
      ) || null
    );
  };

  const setActivePill = (wrap, value) => {
    qsa(".nei-pill", wrap).forEach((b) =>
      b.classList.toggle(
        "is-active",
        b.getAttribute("data-nei-option-value") === value,
      ),
    );
  };

  const optionValueMatchesSelection = (variant, selectedOpts, optionIndex, value) => {
    if (!variant || !Array.isArray(variant.options)) return false;
    if (String(variant.options[optionIndex]) !== String(value)) return false;
    for (let i = 0; i < selectedOpts.length; i += 1) {
      if (i === optionIndex) continue;
      const selected = selectedOpts[i];
      if (selected == null) continue;
      if (String(variant.options[i]) !== String(selected)) return false;
    }
    return true;
  };

  const updateVariantPillAvailability = () => {
    if (!variants.length) return;
    const selectedOpts = getSelectedOptions();
    qsa("[data-nei-option-index]", page).forEach((wrap) => {
      const optionIndex = parseInt(
        wrap.getAttribute("data-nei-option-index") || "-1",
        10,
      );
      if (optionIndex < 0) return;
      qsa(".nei-pill", wrap).forEach((pill) => {
        const value = pill.getAttribute("data-nei-option-value");
        if (!value) return;
        const hasAvailableMatch = variants.some(
          (v) =>
            !!v.available &&
            optionValueMatchesSelection(v, selectedOpts, optionIndex, value),
        );
        const isUnavailable = !hasAvailableMatch;
        // Keep sold-out options clickable so shoppers can still select and preview them.
        pill.removeAttribute("disabled");
        pill.classList.toggle("is-unavailable", isUnavailable);
        if (isUnavailable) {
          pill.setAttribute("title", "Sold out");
        } else {
          pill.removeAttribute("title");
        }
      });
    });
  };

  const getEngravingAddonPriceCents = () => {
    const engravingRoot = qs("[data-nei-engraving]", page);
    if (!engravingRoot) return 0;
    return parseInt(engravingRoot.getAttribute("data-nei-engraving-price") || "0", 10) || 0;
  };

  const updateDisplayedBuyPrice = () => {
    if (!priceEl) return;
    const engravingOn = !!(
      qs("[data-nei-engraving-toggle]", page) &&
      qs("[data-nei-engraving-toggle]", page).checked
    );
    const addonCents = engravingOn ? getEngravingAddonPriceCents() : 0;
    const totalPrice = Math.max(0, activeVariantPriceCents + addonCents);
    priceEl.textContent = window.neiMoney
      ? window.neiMoney(totalPrice)
      : fmtMoney(totalPrice);

    if (comparePriceEl) {
      const hasCompare = activeVariantCompareAtCents > activeVariantPriceCents;
      if (!hasCompare) {
        comparePriceEl.style.display = "none";
      } else {
        comparePriceEl.style.display = "";
        const compareTotal = Math.max(0, activeVariantCompareAtCents + addonCents);
        comparePriceEl.textContent = window.neiMoney
          ? window.neiMoney(compareTotal)
          : fmtMoney(compareTotal);
      }
    }
  };

  const applyVariant = (v) => {
    if (!v) return;
    if (variantIdInput) variantIdInput.value = String(v.id);

    activeVariantPriceCents = parseInt(v.price || 0, 10) || 0;
    activeVariantCompareAtCents = parseInt(v.compare_at_price || 0, 10) || 0;
    updateDisplayedBuyPrice();

    const isVariantAvailable = !!v.available;
    if (addBtn) {
      addBtn.toggleAttribute("disabled", !isVariantAvailable);
      addBtn.textContent = isVariantAvailable ? "Add to cart" : "Sold out";
    }
    if (qtyWrap) {
      qtyWrap.hidden = !isVariantAvailable;
    }
    if (typeof onVariantAvailabilityChanged === "function") {
      onVariantAvailabilityChanged(isVariantAvailable);
    }

    // Stock count + low stock message (optional)
    if (stockWrap && typeof v.inventory_quantity === "number") {
      const count = Math.max(0, parseInt(v.inventory_quantity || 0, 10) || 0);

      const showCount =
        (stockWrap.getAttribute("data-nei-show-count") || "false") === "true";
      const lowEnabled =
        (stockWrap.getAttribute("data-nei-low-stock-enabled") || "false") ===
        "true";
      const thr =
        parseInt(
          stockWrap.getAttribute("data-nei-low-stock-threshold") || "0",
          10,
        ) || 0;
      const tpl = stockWrap.getAttribute("data-nei-low-stock-message") || "";

      // Update stock count text and visibility
      if (stockCountEl) {
        stockCountEl.textContent = String(count);
        // Find the parent stock count wrapper and hide/show it based on setting
        const stockCountWrapper = stockCountEl.closest(".nei-stock-count");
        if (stockCountWrapper) {
          stockCountWrapper.style.display = showCount ? "" : "none";
        }
      }

      // Update low stock message
      if (lowStockEl) {
        if (lowEnabled && v.available && thr > 0 && count > 0 && count <= thr) {
          const msg = tpl
            ? tpl.replace(/\{count\}/g, String(count))
            : `Only ${count} left in stock.`;
          lowStockEl.textContent = msg;
          lowStockEl.style.display = "";
        } else {
          lowStockEl.textContent = "";
          lowStockEl.style.display = "none";
        }
      }

      // Show/hide entire stock wrapper based on whether we have anything to display
      const hasContent =
        (showCount && count > 0) ||
        (lowEnabled && v.available && thr > 0 && count > 0 && count <= thr);

      // If inventory isn't tracked, inventory_quantity may be 0 even when available; hide stock in that case.
      if (
        v.available &&
        count === 0 &&
        (v.inventory_management === null ||
          v.inventory_management === undefined)
      ) {
        stockWrap.style.display = "none";
      } else if (hasContent) {
        stockWrap.style.display = "";
      } else {
        stockWrap.style.display = "none";
      }
    }

    // If the variant has a featured media id, switch the active image.
    if (v.featured_media_id && Number(v.featured_media_id) > 0) {
      const btn = qs(
        `.nei-thumb[data-nei-media-id="${v.featured_media_id}"]`,
        page,
      );
      if (btn) btn.click();
    }

    // Fallback: if no featured media is assigned to the variant, try to match by media alt text
    if (!v.featured_media_id || Number(v.featured_media_id) === 0) {
      const opts = getSelectedOptions().filter(Boolean);
      const needles = [v.name, ...(opts || [])]
        .filter(Boolean)
        .map((s) => String(s).toLowerCase());
      if (needles.length) {
        const thumbs = qsa(".nei-thumb[data-nei-alt]", page);
        const match = thumbs.find((t) => {
          const alt = (t.getAttribute("data-nei-alt") || "").toLowerCase();
          return needles.some((n) => n && alt.includes(n));
        });
        if (match) match.click();
      }
    }

    updateVariantPillAvailability();
  };

  // Click pills
  qsa("[data-nei-option-index]", page).forEach((wrap) => {
    wrap.addEventListener("click", (e) => {
      const pill = e.target.closest(".nei-pill");
      if (!pill) return;
      const val = pill.getAttribute("data-nei-option-value");
      if (!val) return;
      setActivePill(wrap, val);
      const v = findVariant(getSelectedOptions());
      applyVariant(v);
    });
  });

  // ---------------------------
  // Quantity syncing (visible input -> hidden field in form)
  // ---------------------------
  const qtyInput = qs("[data-nei-qty-input]", page);
  const qtyHidden = qs("[data-nei-qty-hidden]", page);
  const syncQty = () => {
    if (!qtyInput || !qtyHidden) return;
    const n = Math.max(1, parseInt(qtyInput.value || "1", 10) || 1);
    qtyInput.value = String(n);
    qtyHidden.value = String(n);
  };
  if (qtyInput) {
    qtyInput.addEventListener("change", syncQty);
    qtyInput.addEventListener("input", syncQty);
  }
  // +/- buttons for this product page
  qsa(".qty-btn[data-nei-qty]", page).forEach((btn) => {
    btn.addEventListener("click", () => {
      if (!qtyInput) return;
      const step = parseInt(btn.getAttribute("data-nei-qty") || "0", 10) || 0;
      const cur = parseInt(qtyInput.value || "1", 10) || 1;
      const next = Math.max(1, cur + step);
      qtyInput.value = String(next);
      syncQty();
    });
  });

  // ---------------------------
  // Engraving add-on + native Shopify line-item file upload
  // ---------------------------
  const engravingWrap = qs("[data-nei-engraving]", page);
  const engravingToggle = qs("[data-nei-engraving-toggle]", page);
  const engravingPill = qs("[data-nei-engraving-pill]", page);
  const engravingPanel = qs("[data-nei-engraving-panel]", page);
  const engravingFileInput = qs("[data-nei-engraving-file]", page);
  const engravingPicker = qs("[data-nei-engraving-picker]", page);
  const engravingPickerMessage = qs("[data-nei-engraving-picker-message]", page);
  const engravingSelectedCard = qs("[data-nei-engraving-selected]", page);
  const engravingSelectedMain = qs(".nei-engraving-selected-main", page);
  const engravingPreviewImg = qs("[data-nei-engraving-preview]", page);
  const engravingPreviewFallback = qs("[data-nei-engraving-preview-fallback]", page);
  const engravingSelectedName = qs("[data-nei-engraving-selected-name]", page);
  const engravingSelectedMeta = qs("[data-nei-engraving-selected-meta]", page);
  const engravingMessage = qs("[data-nei-engraving-message]", page);
  const engravingRemoveBtn = qs("[data-nei-engraving-remove]", page);

  const engravingPropEnabled = qs("[data-nei-engraving-prop-enabled]", form);

  const engravingAddonVariantId = engravingWrap
    ? parseInt(engravingWrap.getAttribute("data-nei-engraving-variant-id") || "0", 10) || 0
    : 0;
  const engravingPriceLabel = engravingWrap
    ? engravingWrap.getAttribute("data-nei-engraving-price-label") || ""
    : "";
  const engravingLabel = engravingWrap
    ? engravingWrap.getAttribute("data-nei-engraving-label") || "Custom Engraving"
    : "Custom Engraving";
  const productTitleText = (qs(".nei-product-title", page)?.textContent || "").trim();

  let engravingSelectedFile = null;
  let engravingPreviewUrl = "";

  const bytesFromMb = (mb) => {
    const n = parseInt(String(mb || "0"), 10);
    return (Number.isFinite(n) && n > 0 ? n : 250) * 1024 * 1024;
  };

  const prettyBytes = (bytes) => {
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    let n = Number(bytes || 0);
    while (n >= 1024 && i < units.length - 1) {
      n /= 1024;
      i++;
    }
    return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  };

  const allowedEngravingExts = ["svg", "dxf"];

  const validateEngravingFile = (file) => {
    if (!file) return { ok: false, hint: "Choose an engraving file." };
    const ext = (String(file.name || "").split(".").pop() || "").toLowerCase();
    if (!allowedEngravingExts.includes(ext)) {
      return { ok: false, hint: "Allowed file types: .svg, .dxf" };
    }
    const limits = getUploadLimits();
    const maxBytes = bytesFromMb(limits.maxMb);
    if (file.size > maxBytes) {
      return {
        ok: false,
        hint: `Max allowed is ${limits.maxMb}MB. Your file is ${prettyBytes(file.size)}.`,
      };
    }
    return { ok: true };
  };

  const clearEngravingPreview = () => {
    if (engravingPreviewUrl) {
      try {
        URL.revokeObjectURL(engravingPreviewUrl);
      } catch {}
      engravingPreviewUrl = "";
    }
    if (engravingPreviewImg) {
      engravingPreviewImg.hidden = true;
      engravingPreviewImg.removeAttribute("src");
    }
    if (engravingPreviewFallback) {
      engravingPreviewFallback.hidden = false;
      engravingPreviewFallback.textContent = "FILE";
    }
  };

  const setEngravingMessage = (message = "", isError = false) => {
    const text = String(message || "").trim();
    const setPickerMessage = (pickerText = "", pickerError = false) => {
      if (!engravingPickerMessage) return;
      const value = String(pickerText || "").trim();
      engravingPickerMessage.hidden = !value;
      engravingPickerMessage.textContent = value;
      engravingPickerMessage.style.color = pickerError ? "#ff6b6b" : "";
    };

    if (!engravingSelectedFile) {
      if (engravingSelectedMain) engravingSelectedMain.hidden = true;
      if (engravingSelectedCard) engravingSelectedCard.hidden = true;
      setPickerMessage(text, isError);
      if (engravingMessage) {
        engravingMessage.hidden = true;
        engravingMessage.textContent = "";
        engravingMessage.style.color = "";
      }
      return;
    }

    setPickerMessage("", false);
    if (!engravingMessage) return;
    engravingMessage.hidden = !text;
    engravingMessage.textContent = text;
    engravingMessage.style.color = isError ? "#ff6b6b" : "";
  };

  const renderEngravingSelectedCard = (file) => {
    const hasFile = !!file;
    if (engravingPicker) engravingPicker.hidden = hasFile;
    if (engravingSelectedMain) engravingSelectedMain.hidden = !hasFile;
    if (engravingSelectedCard) engravingSelectedCard.hidden = !hasFile;
    if (!hasFile) {
      clearEngravingPreview();
      if (engravingSelectedName) engravingSelectedName.textContent = "";
      if (engravingSelectedMeta) engravingSelectedMeta.textContent = "";
      return;
    }

    if (engravingSelectedName) engravingSelectedName.textContent = file.name || "";
    if (engravingSelectedMeta)
      engravingSelectedMeta.textContent = `${prettyBytes(file.size)}`;

    clearEngravingPreview();
    const ext = (String(file.name || "").split(".").pop() || "").toLowerCase();
    if (ext === "svg") {
      try {
        engravingPreviewUrl = URL.createObjectURL(file);
        if (engravingPreviewImg) {
          engravingPreviewImg.src = engravingPreviewUrl;
          engravingPreviewImg.hidden = false;
        }
        if (engravingPreviewFallback) engravingPreviewFallback.hidden = true;
      } catch {
        if (engravingPreviewFallback) {
          engravingPreviewFallback.hidden = false;
          engravingPreviewFallback.textContent = "SVG";
        }
      }
      return;
    }

    if (engravingPreviewFallback) {
      engravingPreviewFallback.hidden = false;
      engravingPreviewFallback.textContent = ext ? ext.toUpperCase() : "FILE";
    }
  };

  const getUploadLimits = () => {
    const raw =
      document.body && document.body.dataset && document.body.dataset.quoteMaxMb
        ? document.body.dataset.quoteMaxMb
        : "250";
    const maxMb = parseInt(raw, 10);
    return { maxMb: Number.isFinite(maxMb) && maxMb > 0 ? maxMb : 250 };
  };

  const clearEngravingSelection = () => {
    engravingSelectedFile = null;
    if (engravingFileInput) engravingFileInput.value = "";
    renderEngravingSelectedCard(null);
    setEngravingMessage("");
  };

  const setEngravingSelectedFile = (file) => {
    const check = validateEngravingFile(file);
    if (!check.ok) {
      clearEngravingSelection();
      setEngravingMessage(check.hint || "Choose a valid file.", true);
      return false;
    }

    engravingSelectedFile = file;
    renderEngravingSelectedCard(file);
    setEngravingMessage("");
    return true;
  };

  const syncEngravingProps = () => {
    const canUse = !!(engravingToggle && !engravingToggle.disabled);
    const on = !!(engravingToggle && engravingToggle.checked && canUse);
    if (engravingPill) engravingPill.classList.toggle("is-active", on);
    if (engravingPanel) engravingPanel.hidden = !on;
    if (engravingFileInput) engravingFileInput.disabled = !(canUse && on);
    updateDisplayedBuyPrice();

    if (!on) {
      if (engravingPropEnabled) engravingPropEnabled.value = "";
      clearEngravingSelection();
      return;
    }

    if (engravingPropEnabled)
      engravingPropEnabled.value = `${engravingLabel} (${engravingPriceLabel})`;
    renderEngravingSelectedCard(engravingSelectedFile);
  };

  const syncEngravingAvailability = (isVariantAvailable) => {
    const enabled = !!isVariantAvailable;
    if (engravingWrap) {
      engravingWrap.classList.toggle("is-disabled", !enabled);
      engravingWrap.setAttribute("aria-disabled", enabled ? "false" : "true");
    }
    if (engravingPill) engravingPill.classList.toggle("is-disabled", !enabled);
    if (engravingToggle) {
      if (!enabled) engravingToggle.checked = false;
      engravingToggle.disabled = !enabled;
    }
    if (!enabled) setEngravingMessage("");
    syncEngravingProps();
  };

  const parseCartErrorMessage = (err) => {
    const raw = String(err?.message || err || "").trim();
    if (!raw) return "";
    try {
      const json = JSON.parse(raw);
      const fromJson = String(
        json?.description || json?.message || json?.error || "",
      ).trim();
      if (fromJson) return fromJson;
    } catch {}
    return raw;
  };

  const EXTRA_GROUP_PROP_KEY = "_nei_extra_group";
  const EXTRA_REF_PROP_KEY = "Panel Engraving ID";

  const makeExtraGroupId = () =>
    `extra_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  const makeExtraRefId = () =>
    `P-${Date.now().toString(36).toUpperCase()}-${Math.random()
      .toString(36)
      .slice(2, 6)
      .toUpperCase()}`;

  const addMainProductWithFormData = async (id, qty, extraProps = null) => {
    if (!form) throw new Error("Missing product form");
    const fd = new FormData(form);
    fd.set("id", String(id));
    fd.set("quantity", String(qty));
    if (extraProps && typeof extraProps === "object") {
      Object.entries(extraProps).forEach(([key, value]) => {
        const propKey = String(key || "").trim();
        if (!propKey) return;
        fd.set(`properties[${propKey}]`, String(value == null ? "" : value));
      });
    }
    const res = await fetch("/cart/add.js", {
      method: "POST",
      body: fd,
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  };

  const extractEngravingFileUrl = (properties) => {
    const props = properties && typeof properties === "object" ? properties : {};
    const raw = String(props["Engraving File"] || props["Engraving File URL"] || "").trim();
    return raw;
  };

  const collectLineItemProperties = () => {
    const props = {};
    if (!form) return props;
    const fd = new FormData(form);
    fd.forEach((value, key) => {
      const m = key.match(/^properties\[(.+)\]$/);
      if (!m) return;
      if (value instanceof File) {
        // JSON /cart/add.js payloads cannot carry file blobs.
        if (!value.name || value.size <= 0) return;
        return;
      }
      const val = String(value == null ? "" : value).trim();
      if (!val) return;
      props[m[1]] = val;
    });
    return props;
  };

  const cartApi = async (url, body) => {
    if (window.neiCart && typeof window.neiCart.api === "function") {
      return await window.neiCart.api(url, body);
    }
    const res = await fetch(url, {
      method: body ? "POST" : "GET",
      headers: body
        ? { "Content-Type": "application/json", Accept: "application/json" }
        : { Accept: "application/json" },
      body: body ? JSON.stringify(body) : undefined,
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  };

  const validateEngravingBeforeAdd = () => {
    const selected = !!(engravingToggle && engravingToggle.checked);
    if (!selected) return { ok: true, enabled: false };

    if (!engravingAddonVariantId) {
      setEngravingMessage("Engraving add-on product is not configured.", true);
      return { ok: false, enabled: true };
    }
    if (!engravingSelectedFile) {
      setEngravingMessage("Choose an engraving file before adding to cart.", true);
      try {
        engravingFileInput && engravingFileInput.focus && engravingFileInput.focus();
      } catch {}
      return { ok: false, enabled: true };
    }
    if (engravingPropEnabled)
      engravingPropEnabled.value = `${engravingLabel} (${engravingPriceLabel})`;
    setEngravingMessage("");

    return { ok: true, enabled: true };
  };
  if (engravingToggle) {
    engravingToggle.addEventListener("change", () => {
      syncEngravingProps();
    });
  }
  if (engravingFileInput) {
    engravingFileInput.addEventListener("change", () => {
      if (!engravingToggle || !engravingToggle.checked) return;
      const file = engravingFileInput.files && engravingFileInput.files[0];
      if (!file) return;
      setEngravingSelectedFile(file);
    });
  }
  if (engravingRemoveBtn) {
    engravingRemoveBtn.addEventListener("click", () => {
      clearEngravingSelection();
    });
  }
  if (engravingPreviewImg) {
    engravingPreviewImg.style.cursor = "zoom-in";
    engravingPreviewImg.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const src = engravingPreviewImg.getAttribute("src") || "";
      if (!src || engravingPreviewImg.hidden) return;
      openLightbox(src, engravingSelectedFile?.name || "Engraving preview");
    });
  }
  onVariantAvailabilityChanged = syncEngravingAvailability;
  syncEngravingProps();

  // ---------------------------
  // Add to cart (AJAX -> cart drawer)
  // ---------------------------
  // We do an explicit AJAX add here to ensure the cart drawer opens reliably even when the button
  // lives outside the <form> via the `form=` attribute.
  if (addBtn) {
    addBtn.addEventListener(
      "click",
      async (e) => {
        // If disabled (sold out), do nothing.
        if (addBtn.hasAttribute("disabled")) return;

        // Prevent the default form submission so we can route through the drawer.
        e.preventDefault();
        e.stopPropagation();

        const id = parseInt(variantIdInput?.value || "0", 10);
        const qty = Math.max(
          1,
          parseInt(qtyHidden?.value || qtyInput?.value || "1", 10) || 1,
        );
        if (!id) return;

        const engravingState = validateEngravingBeforeAdd();
        if (!engravingState.ok) return;

        try {
          if (engravingState.enabled) {
            const extraGroupId = makeExtraGroupId();
            const extraRefId = makeExtraRefId();
            const addedMain = await addMainProductWithFormData(id, qty, {
              [EXTRA_GROUP_PROP_KEY]: extraGroupId,
              [EXTRA_REF_PROP_KEY]: extraRefId,
            });
            const fileUrl = extractEngravingFileUrl(addedMain?.properties);
            const addonProps = {
              "Engraving For": productTitleText || "Product",
              [EXTRA_GROUP_PROP_KEY]: extraGroupId,
              [EXTRA_REF_PROP_KEY]: extraRefId,
            };
            if (fileUrl) addonProps["Engraving File URL"] = fileUrl;
            try {
              await cartApi("/cart/add.js", {
                items: [
                  {
                    id: engravingAddonVariantId,
                    quantity: qty,
                    properties: addonProps,
                  },
                ],
              });
              setEngravingMessage("");
            } catch (addonErr) {
              const addonDetails = parseCartErrorMessage(addonErr);
              setEngravingMessage(
                addonDetails
                  ? `Main item was added, but engraving add-on failed: ${addonDetails}`
                  : "Main item was added, but engraving add-on failed.",
                true,
              );
            }
          } else {
            const mainItem = { id, quantity: qty };
            const mainProps = collectLineItemProperties();
            if (Object.keys(mainProps).length) mainItem.properties = mainProps;
            await cartApi("/cart/add.js", { items: [mainItem] });
          }

          if (window.neiCart && typeof window.neiCart.open === "function") {
            await window.neiCart.open?.(
              document.querySelector("[data-cart-open]") || addBtn,
            );
          } else {
            window.location.href = "/cart";
          }
        } catch (err) {
          console.warn(err);
          if (engravingState.enabled) {
            const details = parseCartErrorMessage(err);
            setEngravingMessage(
              details
                ? `Could not add engraving to cart: ${details}`
                : "Could not add engraving to cart. Please try again.",
              true,
            );
            return;
          }
          // Last-resort fallback for normal products
          form?.submit();
        }
      },
      { passive: false },
    );
  }

  // ---------------------------
  // Smooth scroll links + sticky tabs + scrollspy
  // ---------------------------
  const tabsWrap = qs("[data-nei-tabs]", page);
  const tabs = qsa("[data-nei-tab]", page);
  const sections = qsa("[data-nei-section]", page);

  const scrollToHash = (hash) => {
    const el = hash ? qs(hash, document) : null;
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset;
    const offset =
      (tabsWrap ? tabsWrap.getBoundingClientRect().height : 0) + 12;
    window.scrollTo({ top: Math.max(0, y - offset), behavior: "smooth" });
  };

  tabs.forEach((t) =>
    t.addEventListener("click", () =>
      scrollToHash(t.getAttribute("data-nei-tab")),
    ),
  );
  qsa("[data-nei-scroll]", page).forEach((a) => {
    a.addEventListener("click", (e) => {
      const href = a.getAttribute("href") || "";
      if (!href.startsWith("#")) return;
      e.preventDefault();
      scrollToHash(href);
    });
  });

  const tabsInner =
    tabsWrap && tabsWrap.querySelector(".nei-tabs-inner")
      ? tabsWrap.querySelector(".nei-tabs-inner")
      : tabsWrap;

  let _neiActiveTabId = null;

  const scrollTabIntoView = (el, smooth = false) => {
    if (!el || !tabsInner) return;
    if (tabsInner.scrollWidth <= tabsInner.clientWidth) return;

    const innerRect = tabsInner.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    // Compute element position within the scroll container
    const left = elRect.left - innerRect.left + tabsInner.scrollLeft;
    const right = left + elRect.width;

    const pad = 24; // keeps a little breathing room like Force
    const viewLeft = tabsInner.scrollLeft;
    const viewRight = viewLeft + tabsInner.clientWidth;

    let next = null;
    if (left < viewLeft + pad) next = Math.max(0, left - pad);
    else if (right > viewRight - pad)
      next = Math.max(0, right - tabsInner.clientWidth + pad);

    if (next == null) return;
    try {
      tabsInner.scrollTo({ left: next, behavior: smooth ? "smooth" : "auto" });
    } catch {
      tabsInner.scrollLeft = next;
    }
  };

  const setActiveTab = (id, opts = {}) => {
    if (!id) return;
    const { ensureVisible = false, smooth = false } = opts;

    // Avoid re-running work on every IntersectionObserver tick
    if (_neiActiveTabId === id) return;
    _neiActiveTabId = id;

    let activeEl = null;
    tabs.forEach((t) => {
      const isActive = t.getAttribute("data-nei-tab") === `#${id}`;
      t.classList.toggle("is-active", isActive);
      if (isActive) activeEl = t;
    });

    // IMPORTANT: never call scrollIntoView on scrollspy updates (it can hijack vertical scrolling).
    if (ensureVisible && activeEl) scrollTabIntoView(activeEl, smooth);
  };
  // Scrollspy:
  // Activate tabs in document order as each section top crosses below sticky header + tabs.
  const getStickyOffset = () => {
    const header =
      document.querySelector(".nei-header") || document.querySelector("header");
    const headerH = header ? header.getBoundingClientRect().height : 0;
    const tabsH = tabsWrap ? tabsWrap.getBoundingClientRect().height : 0;
    return Math.max(0, headerH + tabsH);
  };
  // Increase for earlier tab switches, decrease for later switches.
  const tabActivationOffset = 6;

  const updateActiveFromScroll = () => {
    if (!sections.length) return;

    const sy = window.scrollY || window.pageYOffset || 0;
    const sticky = getStickyOffset();
    const triggerLine = sy + sticky + tabActivationOffset;
    let active = sections[0];

    for (const s of sections) {
      const top = s.getBoundingClientRect().top + sy;
      if (top <= triggerLine) active = s;
      else break;
    }

    if (active && active.id) {
      setActiveTab(active.id, { ensureVisible: true, smooth: false });
    }
  };

  let _neiSpyRaf = 0;
  const scheduleSpy = () => {
    if (_neiSpyRaf) return;
    _neiSpyRaf = requestAnimationFrame(() => {
      _neiSpyRaf = 0;
      updateActiveFromScroll();
    });
  };

  window.addEventListener("scroll", scheduleSpy, { passive: true });
  window.addEventListener("resize", scheduleSpy);
  scheduleSpy();

  // ---------------------------
  // Constrained sticky buy card (sticks until the end of the 3-col top section)
  // ---------------------------
  const stickyScope = qs("[data-nei-sticky-scope]", page);
  const stickyCard = qs("[data-nei-sticky-card]", page);
  const stickyTop = 16;
  const mqDesktop = window.matchMedia("(min-width: 992px)");

  const updateStickyCard = () => {
    if (!stickyScope || !stickyCard) return;
    if (!mqDesktop.matches) {
      stickyCard.classList.remove("is-sticky-ended");
      return;
    }
    const scopeTop =
      stickyScope.getBoundingClientRect().top + window.pageYOffset;
    const scopeBottom = scopeTop + stickyScope.offsetHeight;
    const cardH = stickyCard.offsetHeight;
    const y = window.pageYOffset;

    // When the sticky card would extend past the bottom of the scope,
    // pin it to the bottom of the right column instead.
    const shouldEnd = y + stickyTop + cardH >= scopeBottom - 2;
    stickyCard.classList.toggle("is-sticky-ended", shouldEnd);
  };

  let stickyRaf = 0;
  const scheduleStickyUpdate = () => {
    if (stickyRaf) return;
    stickyRaf = requestAnimationFrame(() => {
      stickyRaf = 0;
      updateStickyCard();
    });
  };

  window.addEventListener("scroll", scheduleStickyUpdate, { passive: true });
  window.addEventListener("resize", scheduleStickyUpdate);
  mqDesktop.addEventListener?.("change", scheduleStickyUpdate);
  scheduleStickyUpdate();

  // ---------------------------
  // Sale timer (if present)
  // ---------------------------
  const timer = qs("[data-nei-sale-timer]", page);
  const ends = timer ? timer.getAttribute("data-nei-sale-ends") || "" : "";
  const dd = timer ? qs("[data-nei-timer-dd]", timer) : null;
  const hh = timer ? qs("[data-nei-timer-hh]", timer) : null;
  const mm = timer ? qs("[data-nei-timer-mm]", timer) : null;
  const ss = timer ? qs("[data-nei-timer-ss]", timer) : null;

  const pad2 = (n) => String(Math.max(0, n)).padStart(2, "0");
  const tick = () => {
    if (!timer || !ends) return;
    const endTs = Date.parse(ends);
    if (!Number.isFinite(endTs)) return;
    const now = Date.now();
    let diff = Math.max(0, endTs - now);
    const days = Math.floor(diff / (24 * 3600 * 1000));
    diff -= days * 24 * 3600 * 1000;
    const hours = Math.floor(diff / (3600 * 1000));
    diff -= hours * 3600 * 1000;
    const mins = Math.floor(diff / (60 * 1000));
    diff -= mins * 60 * 1000;
    const secs = Math.floor(diff / 1000);

    if (dd) dd.textContent = pad2(days);
    if (hh) hh.textContent = pad2(hours);
    if (mm) mm.textContent = pad2(mins);
    if (ss) ss.textContent = pad2(secs);
  };

  if (timer && ends) {
    tick();
    setInterval(tick, 1000);
  }

  // Initial variant apply (ensures right rail matches current)
  if (variants.length) {
    const currentId = variantIdInput ? variantIdInput.value : "";
    const v0 =
      variants.find((v) => String(v.id) === String(currentId)) || variants[0];
    applyVariant(v0);
  }
})();

/* ========================================
 * 3) Collection page behavior
 * ====================================== */
(() => {
  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => Array.from(r.querySelectorAll(s));

  const root = qs("[data-nei-collection]");
  if (!root) return;

  const form = qs("#CollectionFiltersForm", root);
  const sort = qs("[data-nei-sort]", root);

  // Auto-submit on sort change (keeps active filters because sort select is inside the same form).
  if (sort && form) {
    sort.addEventListener("change", () => {
      try {
        form.requestSubmit ? form.requestSubmit() : form.submit();
      } catch {
        form.submit();
      }
    });
  }

  // View toggle (grid/list) with persistence
  const grid = qs("[data-nei-grid]", root);
  const viewBtns = qsa("[data-nei-view]", root);
  const STORAGE_KEY = "nei_collection_view_v1";

  const applyView = (mode) => {
    if (!grid) return;
    const m = mode === "list" ? "list" : "grid";
    grid.classList.toggle("is-list", m === "list");
    viewBtns.forEach((b) => {
      const active = b.getAttribute("data-nei-view") === m;
      b.classList.toggle("is-active", active);
      b.setAttribute("aria-pressed", active ? "true" : "false");
    });
    try {
      localStorage.setItem(STORAGE_KEY, m);
    } catch {}
  };

  const saved = (() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  })();
  applyView(saved || "grid");

  viewBtns.forEach((btn) => {
    btn.addEventListener("click", () =>
      applyView(btn.getAttribute("data-nei-view")),
    );
  });

  // Mobile filters drawer: clone the filters card into the drawer slot.
  const drawer = qs("[data-nei-filter-drawer]", root);
  const openBtn = qs("[data-nei-open-filters]", root);
  const closeBtns = qsa("[data-nei-close-filters]", root);
  const slot = qs("[data-nei-filter-drawer-slot]", root);
  const desktopCard = qs(".nei-facets-card", root);
  const sortDrawer = qs("[data-nei-sort-drawer]", root);
  const sortOpenBtn = qs("[data-nei-open-sort]", root);
  const sortCloseBtns = qsa("[data-nei-close-sort]", root);
  const sortOptionBtns = qsa("[data-nei-sort-option]", root);

  const lockScroll = (on) => {
    document.documentElement.classList.toggle("nei-lock", !!on);
  };

  const openDrawer = () => {
    if (!drawer || !slot || !desktopCard) return;
    slot.innerHTML = "";
    slot.appendChild(desktopCard.cloneNode(true));
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add("is-open"));
    lockScroll(true);

    // Ensure the cloned form submits correctly.
    const cloneForm = qs("#CollectionFiltersForm", drawer);
    const cloneSort = qs("[data-nei-sort]", drawer);
    if (cloneSort && cloneForm) {
      cloneSort.addEventListener("change", () => {
        try {
          cloneForm.requestSubmit
            ? cloneForm.requestSubmit()
            : cloneForm.submit();
        } catch {
          cloneForm.submit();
        }
      });
    }
  };

  const closeDrawer = () => {
    if (!drawer) return;
    drawer.classList.remove("is-open");
    setTimeout(() => {
      if (drawer) drawer.hidden = true;
    }, 220);
    lockScroll(false);
  };

  const openSortDrawer = () => {
    if (!sortDrawer) return;
    sortDrawer.hidden = false;
    requestAnimationFrame(() => sortDrawer.classList.add("is-open"));
    lockScroll(true);
  };

  const closeSortDrawer = () => {
    if (!sortDrawer) return;
    sortDrawer.classList.remove("is-open");
    setTimeout(() => {
      if (sortDrawer) sortDrawer.hidden = true;
    }, 220);
    lockScroll(false);
  };

  if (openBtn) openBtn.addEventListener("click", openDrawer);
  closeBtns.forEach((b) => b.addEventListener("click", closeDrawer));
  if (sortOpenBtn) sortOpenBtn.addEventListener("click", openSortDrawer);
  sortCloseBtns.forEach((b) => b.addEventListener("click", closeSortDrawer));
  sortOptionBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      if (!sort || !form) return;
      const value = btn.getAttribute("data-nei-sort-option");
      if (value) sort.value = value;
      try {
        form.requestSubmit ? form.requestSubmit() : form.submit();
      } catch {
        form.submit();
      }
      closeSortDrawer();
    });
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (drawer && !drawer.hidden) closeDrawer();
    if (sortDrawer && !sortDrawer.hidden) closeSortDrawer();
  });
})();

/* ========================================
 * 4) Product mobile read-more behavior
 * ====================================== */
(() => {
  const getWrappers = () =>
    Array.from(document.querySelectorAll("[data-nei-readmore]"));

  const setHeight = (wrapper) => {
    const full = wrapper.querySelector(".nei-desc-full");
    if (!full) return;
    wrapper.style.setProperty("--nei-desc-max", `${full.scrollHeight}px`);
  };

  const refreshAll = () => {
    getWrappers().forEach((wrapper) => setHeight(wrapper));
  };

  window.addEventListener("load", refreshAll, { once: true });
  window.addEventListener("resize", () => {
    if (window.innerWidth > 992) return;
    refreshAll();
  });

  document.addEventListener("click", (event) => {
    const toggle = event.target.closest("[data-nei-readmore-toggle]");
    if (!toggle) return;
    const wrapper = toggle.closest("[data-nei-readmore]");
    if (!wrapper) return;
    const scrollY = window.scrollY;
    const isOpen = wrapper.classList.toggle("is-open");
    toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
    toggle.textContent = isOpen ? "Show less" : "Read more";
    setHeight(wrapper);
    window.scrollTo({ top: scrollY });
  });
})();

/* ========================================
 * 5) Quote modal + secure upload behavior
 * ====================================== */
(function () {
  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }
  function setText(el, text) {
    if (el) el.textContent = text || "";
  }

  function openModal(modal) {
    if (!modal) return;

    // Store original body HTML on first open
    var body = qs(".nei-quote-modal__body", modal);
    if (body && !modal._originalBodyHTML) {
      modal._originalBodyHTML = body.innerHTML;
    }

    // Reset modal to form view if it was showing success message
    var form = qs("[data-quote-form]", modal);
    if (form && form.style.display === "none") {
      // Restore original body HTML
      if (body && modal._originalBodyHTML) {
        body.innerHTML = modal._originalBodyHTML;
      }

      // Clear file selection state
      modal._quoteSelectedFiles = [];
      modal._quoteUploadedFiles = [];
      modal._quoteUploading = false;
    }

    modal.style.display = "block";
    modal.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("nei-modal-open");

    // Force reflow to trigger animation
    modal.offsetHeight;
    modal.classList.add("is-open");
  }

  function closeModal(modal) {
    if (!modal) return;
    modal.setAttribute("aria-hidden", "true");
    modal.classList.remove("is-open");
    document.documentElement.classList.remove("nei-modal-open");

    // Wait for animation to finish before hiding (300ms to be safe)
    setTimeout(function () {
      if (!modal.classList.contains("is-open")) {
        modal.style.display = "none";
      }
    }, 300);
  }

  function bytesFromMb(mb) {
    var n = parseInt(mb, 10);
    if (!isFinite(n) || n <= 0) n = 250;
    return n * 1024 * 1024;
  }

  function getLimits() {
    var maxMb =
      document.body && document.body.dataset && document.body.dataset.quoteMaxMb
        ? document.body.dataset.quoteMaxMb
        : "250";
    var retentionDays =
      document.body &&
      document.body.dataset &&
      document.body.dataset.quoteRetentionDays
        ? document.body.dataset.quoteRetentionDays
        : "30";
    return {
      maxMb: parseInt(maxMb, 10) || 250,
      retentionDays: parseInt(retentionDays, 10) || 30,
    };
  }

  function prettyBytes(bytes) {
    var units = ["B", "KB", "MB", "GB"];
    var i = 0;
    var n = Number(bytes || 0);
    while (n >= 1024 && i < units.length - 1) {
      n /= 1024;
      i++;
    }
    return n.toFixed(i === 0 ? 0 : 1) + " " + units[i];
  }

  function fileSignature(file) {
    return [file.name, file.size, file.lastModified].join(":");
  }

  function fileCountLabel(count, suffix) {
    return count + " file" + (count === 1 ? "" : "s") + (suffix || "");
  }

  function absoluteQuoteUrl(url) {
    try {
      return new URL(url, window.location.origin).href;
    } catch (e) {
      return url;
    }
  }

  function getAttachmentFields(modal) {
    return {
      name: qs('[data-quote-field="file_name"]', modal),
      url: qs('[data-quote-field="file_url"]', modal),
      size: qs('[data-quote-field="file_size"]', modal),
      count: qs('[data-quote-field="attachment_count"]', modal),
      attachments: qs('[data-quote-field="attachments"]', modal),
      attachmentsJson: qs('[data-quote-field="attachments_json"]', modal),
    };
  }

  function clearAttachmentFields(modal) {
    var fields = getAttachmentFields(modal);
    if (fields.name) fields.name.value = "";
    if (fields.url) fields.url.value = "";
    if (fields.size) fields.size.value = "";
    if (fields.count) fields.count.value = "0";
    if (fields.attachments) fields.attachments.value = "";
    if (fields.attachmentsJson) fields.attachmentsJson.value = "[]";
  }

  function syncAttachmentFields(modal, uploads) {
    var fields = getAttachmentFields(modal);
    var completed = (uploads || []).filter(Boolean);
    var first = completed[0];

    // Keep the original fields populated for compatibility with the existing
    // email worker, and provide the complete list in the Attachments field.
    if (fields.name) fields.name.value = first ? first.name : "";
    if (fields.url) fields.url.value = first ? first.url : "";
    if (fields.size) fields.size.value = first ? first.displaySize : "";
    if (fields.count) fields.count.value = String(completed.length);
    if (fields.attachments) {
      fields.attachments.value = completed
        .map(function (upload, index) {
          return (
            index +
            1 +
            ". " +
            upload.name +
            " (" +
            upload.displaySize +
            "): " +
            absoluteQuoteUrl(upload.url)
          );
        })
        .join("\n");
    }
    if (fields.attachmentsJson) {
      fields.attachmentsJson.value = JSON.stringify(
        completed.map(function (upload) {
          return {
            name: upload.name,
            size: upload.displaySize,
            url: absoluteQuoteUrl(upload.url),
          };
        }),
      );
    }
  }

  function reconcileUploadedFiles(modal, files) {
    var previousUploads = (modal._quoteUploadedFiles || []).filter(Boolean);
    return files.map(function (file) {
      var signature = fileSignature(file);
      return (
        previousUploads.find(function (upload) {
          return upload.signature === signature;
        }) || null
      );
    });
  }

  function renderFileQueue(modal) {
    var queue = qs("[data-quote-file-queue]", modal);
    var count = qs("[data-quote-file-count]", modal);
    var list = qs("[data-quote-file-list]", modal);
    var files = modal._quoteSelectedFiles || [];

    if (!queue || !list) return;
    queue.hidden = !files.length;
    setText(count, files.length ? fileCountLabel(files.length, " queued") : "");
    list.innerHTML = "";

    var maxBytes = bytesFromMb(getLimits().maxMb);
    files.forEach(function (file, index) {
      var isTooLarge = file.size > maxBytes;
      var item = document.createElement("div");
      item.className =
        "nei-quote-file-item" + (isTooLarge ? " is-invalid" : "");

      var details = document.createElement("div");
      details.className = "nei-quote-file-item__details";

      var name = document.createElement("div");
      name.className = "nei-quote-file-item__name";
      name.textContent = file.name;
      name.title = file.name;

      var size = document.createElement("div");
      size.className = "nei-quote-file-item__size";
      size.textContent =
        prettyBytes(file.size) + (isTooLarge ? " — exceeds size limit" : "");

      var remove = document.createElement("button");
      remove.type = "button";
      remove.className = "nei-quote-file-remove";
      remove.setAttribute("data-quote-file-remove", String(index));
      remove.setAttribute("aria-label", "Remove " + file.name);
      remove.title = "Remove file";
      remove.textContent = "×";
      remove.disabled = Boolean(modal._quoteUploading);

      details.appendChild(name);
      details.appendChild(size);
      item.appendChild(details);
      item.appendChild(remove);
      list.appendChild(item);
    });
  }

  function showQueuedFilesStatus(modal, extraHint) {
    var files = modal._quoteSelectedFiles || [];
    renderFileQueue(modal);

    if (!files.length) {
      setUploadUI(modal, {
        show: false,
        name: "",
        status: "",
        pct: 0,
        hint: "",
      });
      return;
    }

    var limits = getLimits();
    var maxBytes = bytesFromMb(limits.maxMb);
    var oversizedFile = files.find(function (file) {
      return file.size > maxBytes;
    });

    if (oversizedFile) {
      setUploadUI(modal, {
        show: true,
        name: oversizedFile.name,
        status: "File too large",
        pct: 0,
        hint:
          "Max allowed is " +
          limits.maxMb +
          "MB. This file is " +
          prettyBytes(oversizedFile.size) +
          ". Remove it to continue.",
      });
      return;
    }

    var uploadedCount = reconcileUploadedFiles(modal, files).filter(
      Boolean,
    ).length;
    var hint = "";
    if (uploadedCount === files.length) {
      hint = "All queued files are already uploaded and ready to send.";
    } else if (uploadedCount) {
      hint =
        fileCountLabel(uploadedCount) +
        " already uploaded; remaining files will upload when you send the request.";
    } else {
      hint = "Queued files will upload when you click Send request.";
    }
    if (extraHint) hint += " " + extraHint;

    setUploadUI(modal, {
      show: true,
      name: fileCountLabel(files.length, " queued"),
      status: "Ready",
      pct: 0,
      hint: hint,
    });
  }

  function setQuoteBusy(modal, isBusy) {
    modal._quoteUploading = isBusy;
    var submitBtn = qs("[data-quote-submit]", modal);
    var fileInput = qs("[data-quote-file]", modal);
    if (submitBtn) submitBtn.disabled = isBusy;
    if (fileInput) fileInput.disabled = isBusy;
    renderFileQueue(modal);
  }

  function setUploadUI(modal, state) {
    var box = qs("[data-quote-upload]", modal);
    var n = qs("[data-quote-upload-name]", modal);
    var s = qs("[data-quote-upload-status]", modal);
    var bar = qs("[data-quote-upload-bar]", modal);
    var h = qs("[data-quote-upload-hint]", modal);

    if (box) box.hidden = !state.show;
    setText(n, state.name);
    setText(s, state.status);
    setText(h, state.hint);
    if (bar) bar.style.width = Math.max(0, Math.min(100, state.pct || 0)) + "%";

    // Apply error styling if status indicates failure
    var isError =
      state.status &&
      (state.status.toLowerCase().includes("fail") ||
        state.status.toLowerCase().includes("error") ||
        state.status === "File too large" ||
        state.status === "Login required");
    if (s) s.style.color = isError ? "#ff6b6b" : "";
    if (h) h.style.color = isError ? "#ff6b6b" : "";
  }

  function getSimpleErrorMessage(err) {
    // Convert technical error messages to user-friendly ones
    var message = err?.message || err || "";

    // Rate limiting / quota errors
    if (message.includes("429") || message.includes("Rate limit")) {
      return "Max number of uploads allowed for today exceeded";
    }

    // File size errors
    if (message.includes("File too large")) {
      return "File is too large. Please check the size limit.";
    }

    // Authentication errors
    if (
      message.includes("Login required") ||
      message.includes("401") ||
      message.includes("403")
    ) {
      return "You must be logged in to upload files";
    }

    // Network/CORS errors
    if (
      message.includes("/apps/quote-upload/sign") ||
      message.includes("CORS")
    ) {
      return "Network error. Please check your connection and try again.";
    }

    // Server errors
    if (message.includes("5")) {
      return "Server error. Please try again later.";
    }

    // Default message
    return "Something went wrong. Please try again.";
  }

  function showSuccessMessage(modal) {
    // Hide the form
    var form = qs("[data-quote-form]", modal);
    if (form) form.style.display = "none";

    // Update modal body with success message
    var body = qs(".nei-quote-modal__body", modal);
    if (!body) return;

    body.innerHTML =
      '<div style="text-align:center; padding: 40px 20px;">' +
      '<div style="font-size: 48px; margin-bottom: 20px;">✓</div>' +
      '<h3 style="font-size: 22px; font-weight: 800; margin-bottom: 12px; color: var(--text);">Thank you for your submission!</h3>' +
      '<p style="color: var(--muted); margin-bottom: 30px; font-size: 15px;">We will review your quote request and get back to you shortly.</p>' +
      '<button type="button" class="nei-cta-btn" data-quote-close style="padding: 12px 32px;">Close</button>' +
      "</div>";

    // Reset upload state
    modal._quoteUploading = false;
    modal._quoteSelectedFiles = [];
    modal._quoteUploadedFiles = [];
  }

  function getCustomerId() {
    var el =
      document.getElementById("neiQuoteConfig") ||
      document.getElementById("neiSettings");
    var v = el && el.getAttribute("data-customer-id");
    return (v || "").trim();
  }

  function getCurrentVariantId() {
    var el = document.querySelector("[data-nei-variant-id]");
    return el && el.value ? String(el.value) : "";
  }

  function getCurrentVariantTitle() {
    // Try active pill selections -> combined
    var pills = document.querySelectorAll(
      ".nei-option-values .nei-pill.is-active",
    );
    if (!pills || !pills.length) return "";
    var parts = [];
    pills.forEach(function (p) {
      parts.push((p.textContent || "").trim());
    });
    return parts.join(" / ");
  }

  function getCurrentQty() {
    var el = document.querySelector("[data-nei-qty-input]");
    var v = el ? parseInt(el.value, 10) : 1;
    return !isFinite(v) || v < 1 ? 1 : v;
  }

  function fillModal(modal, trigger) {
    var title = trigger.getAttribute("data-quote-product-title") || "";
    var url = trigger.getAttribute("data-quote-product-url") || "";

    // Variant can change after render; always read current selection
    var variantId =
      getCurrentVariantId() ||
      trigger.getAttribute("data-quote-variant-id") ||
      "";
    var variantTitle =
      getCurrentVariantTitle() ||
      trigger.getAttribute("data-quote-variant-title") ||
      "";

    var subtitle = title;
    if (variantTitle && variantTitle !== "Default Title")
      subtitle += " — " + variantTitle;
    setText(qs("[data-quote-subtitle]", modal), subtitle);

    var productField = qs('[data-quote-field="product"]', modal);
    var variantField = qs('[data-quote-field="variant"]', modal);
    var variantIdField = qs('[data-quote-field="variant_id"]', modal);
    var urlField = qs('[data-quote-field="url"]', modal);

    if (productField) productField.value = title;
    if (variantField) variantField.value = variantTitle;
    if (variantIdField) variantIdField.value = variantId;
    if (urlField) urlField.value = window.location.origin + url;

    // Qty default from product page control
    var qty = getCurrentQty();
    var qtyInput = qs("[data-quote-qty]", modal);
    if (qtyInput) qtyInput.value = String(qty);

    // Reset attachment fields
    clearAttachmentFields(modal);

    var uploadBox = qs("[data-quote-upload]", modal);
    if (uploadBox) uploadBox.hidden = true;

    var fileInput = qs("[data-quote-file]", modal);
    if (fileInput) fileInput.value = "";

    modal._quoteSelectedFiles = [];
    modal._quoteUploadedFiles = [];
    setQuoteBusy(modal, false);
  }

  async function requestPresign(payload) {
    // payload expected: { filename, size, type, ... }
    // Build querystring so the request is a simple GET (Shopify app proxy behaves best here).
    var qs = new URLSearchParams();

    if (payload && payload.filename)
      qs.set("filename", String(payload.filename));
    if (payload && payload.size != null) qs.set("size", String(payload.size));
    if (payload && payload.type) qs.set("type", String(payload.type));

    // Optional extras if you pass them:
    if (payload && payload.product_handle)
      qs.set("product", String(payload.product_handle));
    if (payload && payload.variant_id)
      qs.set("variant_id", String(payload.variant_id));
    if (payload && payload.qty != null) qs.set("qty", String(payload.qty));

    var url =
      "/apps/quote-upload/sign" + (qs.toString() ? "?" + qs.toString() : "");

    var res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });

    if (!res.ok) {
      var t = "";
      try {
        t = await res.text();
      } catch (e) {}
      var errorMsg =
        "Sign failed (" + res.status + "): " + (t || "Unknown error");
      throw new Error(errorMsg);
    }

    // Expecting: { ok:true, uploadUrl, fileUrl, ... }
    return await res.json();
  }

  function putWithProgress(uploadUrl, file, onProgress) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl, true);

      // IMPORTANT: don't send cookies/credentials to R2
      xhr.withCredentials = false;

      xhr.upload.onprogress = function (e) {
        if (!e.lengthComputable) return;
        var pct = Math.round((e.loaded / e.total) * 100);
        onProgress(pct);
      };

      xhr.onload = function () {
        // If the browser can read it, grab it for debugging
        var body = "";
        try {
          body = xhr.responseText || "";
        } catch (e) {}

        if (xhr.status >= 200 && xhr.status < 300) return resolve();

        reject(
          new Error(
            "Upload failed (" +
              xhr.status +
              "): " +
              (body ? body.slice(0, 600) : "No response body"),
          ),
        );
      };

      xhr.onerror = function () {
        // This is what you see when response is blocked by CORS (status 0)
        reject(new Error("/apps/quote-upload/sign"));
      };

      xhr.send(file);
    });
  }

  async function handleFilesUpload(modal, files) {
    var limits = getLimits();
    var maxBytes = bytesFromMb(limits.maxMb);
    var oversizedFile = files.find(function (file) {
      return file.size > maxBytes;
    });

    if (oversizedFile) {
      setUploadUI(modal, {
        show: true,
        name: oversizedFile.name,
        status: "File too large",
        pct: 0,
        hint:
          "Max allowed is " +
          limits.maxMb +
          "MB. Your file is " +
          prettyBytes(oversizedFile.size) +
          ".",
      });
      throw new Error("File too large: " + oversizedFile.name);
    }

    var variantId =
      (qs('[data-quote-field="variant_id"]', modal) || {}).value || "";
    var productUrl = (qs('[data-quote-field="url"]', modal) || {}).value || "";
    var customerId = getCustomerId();

    if (!customerId) {
      setUploadUI(modal, {
        show: true,
        name: fileCountLabel(files.length, " selected"),
        status: "Login required",
        pct: 0,
        hint: "Please log in to attach files to a quote request.",
      });
      throw new Error("Login required");
    }

    setQuoteBusy(modal, true);

    var totalBytes = files.reduce(function (total, file) {
      return total + file.size;
    }, 0);
    var completedBytes = 0;
    var previousUploads = reconcileUploadedFiles(modal, files);
    var uploads = new Array(files.length);

    try {
      for (let index = 0; index < files.length; index++) {
        const file = files[index];
        var signature = fileSignature(file);
        var previous = previousUploads[index];

        if (previous && previous.signature === signature) {
          uploads[index] = previous;
          completedBytes += file.size;
          continue;
        }

        setUploadUI(modal, {
          show: true,
          name: file.name + " (" + (index + 1) + " of " + files.length + ")",
          status: "Preparing upload…",
          pct: Math.round((completedBytes / totalBytes) * 100),
          hint: "File size: " + prettyBytes(file.size),
        });

        var presign = await requestPresign({
          logged_in_customer_id: customerId,
          filename: file.name,
          size: file.size,
          type: file.type || "application/octet-stream",
          variant_id: variantId || null,
          product_url: productUrl || null,
          max_mb: limits.maxMb,
          retention_days: limits.retentionDays,
        });

        setUploadUI(modal, {
          show: true,
          name: file.name + " (" + (index + 1) + " of " + files.length + ")",
          status: "Uploading…",
          pct: Math.round((completedBytes / totalBytes) * 100),
          hint: "Uploading to secure storage…",
        });

        await putWithProgress(presign.uploadUrl, file, function (pct) {
          var loadedBytes = completedBytes + file.size * (pct / 100);
          var totalPct = Math.round((loadedBytes / totalBytes) * 100);
          setUploadUI(modal, {
            show: true,
            name:
              file.name + " (" + (index + 1) + " of " + files.length + ")",
            status: "Uploading…",
            pct: totalPct,
            hint: "Overall progress: " + totalPct + "%",
          });
        });

        uploads[index] = {
          signature: signature,
          name: file.name,
          url: absoluteQuoteUrl(presign.fileUrl),
          displaySize: prettyBytes(file.size),
        };
        completedBytes += file.size;
        modal._quoteUploadedFiles = uploads.slice();
        syncAttachmentFields(modal, uploads);
      }

      modal._quoteUploadedFiles = uploads;
      syncAttachmentFields(modal, uploads);
      setUploadUI(modal, {
        show: true,
        name: fileCountLabel(files.length),
        status: "Uploaded",
        pct: 100,
        hint:
          files.length === 1
            ? "File attached to quote request as a secure link."
            : "All files attached to quote request as secure links.",
      });
      return uploads;
    } finally {
      setQuoteBusy(modal, false);
    }
  }

  document.addEventListener("click", function (e) {
    var openBtn = e.target.closest && e.target.closest("[data-quote-open]");
    var closeBtn = e.target.closest && e.target.closest("[data-quote-close]");
    var removeFileBtn =
      e.target.closest && e.target.closest("[data-quote-file-remove]");
    var backdrop =
      e.target.closest &&
      e.target.closest("[data-quote-modal] > .nei-quote-modal__backdrop");
    var modal = qs("[data-quote-modal]");

    if (removeFileBtn) {
      modal = removeFileBtn.closest("[data-quote-modal]") || modal;
      if (!modal || modal._quoteUploading) {
        e.preventDefault();
        return;
      }

      var removeIndex = parseInt(
        removeFileBtn.getAttribute("data-quote-file-remove"),
        10,
      );
      var queuedFiles = (modal._quoteSelectedFiles || []).slice();
      if (isFinite(removeIndex) && queuedFiles[removeIndex]) {
        queuedFiles.splice(removeIndex, 1);
        modal._quoteSelectedFiles = queuedFiles;
        modal._quoteUploadedFiles = reconcileUploadedFiles(modal, queuedFiles);
        syncAttachmentFields(modal, modal._quoteUploadedFiles);
        showQueuedFilesStatus(modal);
      }
      e.preventDefault();
      return;
    }

    if (openBtn) {
      if (!modal) return;
      fillModal(modal, openBtn);
      openModal(modal);
      e.preventDefault();
      return;
    }
    if (closeBtn) {
      // Don't close if upload is in progress
      if (modal && modal._quoteUploading) {
        e.preventDefault();
        return;
      }
      closeModal(modal);
      e.preventDefault();
    }
    // Also prevent backdrop click from closing if upload is in progress
    if (backdrop) {
      if (modal && modal._quoteUploading) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      closeModal(modal);
      e.preventDefault();
    }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    var modal = document.querySelector("[data-quote-modal].is-open");
    if (!modal) return;
    // Don't close if upload is in progress
    if (modal._quoteUploading) return;
    closeModal(modal);
  });

  document.addEventListener("change", function (e) {
    var input = e.target.closest && e.target.closest("[data-quote-file]");
    if (!input) return;

    var modal =
      document.querySelector("[data-quote-modal].is-open") ||
      qs("[data-quote-modal]");
    if (!modal) return;

    var incomingFiles = Array.from(input.files || []);
    var queuedFiles = (modal._quoteSelectedFiles || []).slice();
    var queuedSignatures = new Set(
      queuedFiles.map(function (file) {
        return fileSignature(file);
      }),
    );
    var duplicateCount = 0;

    incomingFiles.forEach(function (file) {
      var signature = fileSignature(file);
      if (queuedSignatures.has(signature)) {
        duplicateCount++;
        return;
      }
      queuedSignatures.add(signature);
      queuedFiles.push(file);
    });

    modal._quoteSelectedFiles = queuedFiles;
    modal._quoteUploadedFiles = reconcileUploadedFiles(modal, queuedFiles);
    syncAttachmentFields(modal, modal._quoteUploadedFiles);

    // The visible queue is the source of truth. Clearing the native control
    // lets choosing the same file later fire another change event.
    input.value = "";

    var duplicateHint = "";
    if (duplicateCount) {
      duplicateHint = fileCountLabel(duplicateCount, " already queued") + ".";
    }
    showQueuedFilesStatus(modal, duplicateHint);

    // Re-enable submit button when new file is selected (in case it was disabled from previous error)
    var submitBtn = qs("[data-quote-submit]", modal);
    if (submitBtn) submitBtn.disabled = false;
  });

  document.addEventListener(
    "submit",
    async function (e) {
      const form = e.target.closest?.("[data-quote-form]");
      if (!form) return;

      const modal = form.closest("[data-quote-modal]");
      if (!modal) return;

      // Always prevent default - we'll handle submission via AJAX
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();

      const files = modal._quoteSelectedFiles || [];

      try {
        // Upload each selected file before sending the request.
        if (files.length) {
          await handleFilesUpload(modal, files);
        }

        // Submit form data to worker endpoint
        const formData = new FormData(form);

        // Extract contact fields and flatten them for the email
        const emailData = {};
        for (let [key, value] of formData.entries()) {
          // Extract field name from contact[fieldName]
          const match = key.match(/contact\[(.*?)\]/);
          if (match) {
            // Convert field name to snake_case: "File Name" -> "file_name"
            const fieldName = match[1].toLowerCase().replace(/\s+/g, "_");
            emailData[fieldName] = value;
          } else if (
            !key.startsWith("contact[") &&
            !key.startsWith("form_type") &&
            !key.startsWith("utf8")
          ) {
            // Include non-contact fields as-is
            emailData[key] = value;
          }
        }

        // Convert back to FormData with simple field names for worker
        const cleanFormData = new FormData();
        for (let [key, value] of Object.entries(emailData)) {
          cleanFormData.append(key, value);
        }

        setQuoteBusy(modal, true);

        const response = await fetch("/apps/quote-upload/send-email", {
          method: "POST",
          body: cleanFormData,
        });

        const result = await response.json();

        if (result.success) {
          // Show success message in modal
          showSuccessMessage(modal);
        } else {
          // Show error in upload UI
          setUploadUI(modal, {
            show: true,
            name: files.length ? fileCountLabel(files.length) : "",
            status: "Error",
            pct: 0,
            hint: getSimpleErrorMessage(result.error),
          });
          setQuoteBusy(modal, false);
        }
      } catch (err) {
        // Show error in upload UI
        setUploadUI(modal, {
          show: true,
          name: files.length ? fileCountLabel(files.length) : "",
          status: "Error",
          pct: 0,
          hint: getSimpleErrorMessage(err),
        });
        setQuoteBusy(modal, false);
      }
    },
    true,
  );
})();



